import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import React__default, { forwardRef, useRef, useCallback, useEffect, useMemo, useState, createContext, useContext } from "react";
import { Box, KeyboardFocusOnly, ButtonLink, VisuallyHidden, Text, Button, CharacterCounter, Icon as Icon$1, Flex, Skeleton, Input } from "@indeed/ifl-components";
import { withEmotionCache, ThemeContext, useTheme } from "@emotion/react";
import { useResponsive } from "@indeed/indeed-hook-library";
import useId from "@indeed/ihl-use-id";
import isPropValid from "@emotion/is-prop-valid";
import { FadeInOut, AnimatePresence } from "@indeed/jspl-animation";
import { useI18n } from "@indeed/jspl-i18n";
function getDefaultExportFromCjs(e) {
  return e && e.__esModule && Object.prototype.hasOwnProperty.call(e, "default") ? e.default : e;
}
var isMergeableObject = function(r) {
  return isNonNullObject(r) && !isSpecial(r);
};
function isNonNullObject(e) {
  return !!e && typeof e == "object";
}
function isSpecial(e) {
  var r = Object.prototype.toString.call(e);
  return r === "[object RegExp]" || r === "[object Date]" || isReactElement(e);
}
var canUseSymbol = typeof Symbol == "function" && Symbol.for, REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for("react.element") : 60103;
function isReactElement(e) {
  return e.$$typeof === REACT_ELEMENT_TYPE;
}
function emptyTarget(e) {
  return Array.isArray(e) ? [] : {};
}
function cloneUnlessOtherwiseSpecified(e, r) {
  return r.clone !== !1 && r.isMergeableObject(e) ? deepmerge(emptyTarget(e), e, r) : e;
}
function defaultArrayMerge(e, r, n) {
  return e.concat(r).map(function(a) {
    return cloneUnlessOtherwiseSpecified(a, n);
  });
}
function getMergeFunction(e, r) {
  if (!r.customMerge)
    return deepmerge;
  var n = r.customMerge(e);
  return typeof n == "function" ? n : deepmerge;
}
function getEnumerableOwnPropertySymbols(e) {
  return Object.getOwnPropertySymbols ? Object.getOwnPropertySymbols(e).filter(function(r) {
    return Object.propertyIsEnumerable.call(e, r);
  }) : [];
}
function getKeys(e) {
  return Object.keys(e).concat(getEnumerableOwnPropertySymbols(e));
}
function propertyIsOnObject(e, r) {
  try {
    return r in e;
  } catch {
    return !1;
  }
}
function propertyIsUnsafe(e, r) {
  return propertyIsOnObject(e, r) && !(Object.hasOwnProperty.call(e, r) && Object.propertyIsEnumerable.call(e, r));
}
function mergeObject(e, r, n) {
  var a = {};
  return n.isMergeableObject(e) && getKeys(e).forEach(function(o) {
    a[o] = cloneUnlessOtherwiseSpecified(e[o], n);
  }), getKeys(r).forEach(function(o) {
    propertyIsUnsafe(e, o) || (propertyIsOnObject(e, o) && n.isMergeableObject(r[o]) ? a[o] = getMergeFunction(o, n)(e[o], r[o], n) : a[o] = cloneUnlessOtherwiseSpecified(r[o], n));
  }), a;
}
function deepmerge(e, r, n) {
  n = n || {}, n.arrayMerge = n.arrayMerge || defaultArrayMerge, n.isMergeableObject = n.isMergeableObject || isMergeableObject, n.cloneUnlessOtherwiseSpecified = cloneUnlessOtherwiseSpecified;
  var a = Array.isArray(r), o = Array.isArray(e), i = a === o;
  return i ? a ? n.arrayMerge(e, r, n) : mergeObject(e, r, n) : cloneUnlessOtherwiseSpecified(r, n);
}
deepmerge.all = function(r, n) {
  if (!Array.isArray(r))
    throw new Error("first argument should be an array");
  return r.reduce(function(a, o) {
    return deepmerge(a, o, n);
  }, {});
};
var deepmerge_1 = deepmerge, cjs = deepmerge_1;
const merge = /* @__PURE__ */ getDefaultExportFromCjs(cjs), DEFAULT_KEY = "[base]", hasDefault = (e) => typeof e == "object" && e !== null && DEFAULT_KEY in e;
function get(e, r, n, a, o) {
  const i = r && typeof r == "string" ? r.split(".") : [r];
  for (a = 0; a < i.length; a++)
    e = e ? e[i[a]] : o;
  return e === o ? n : hasDefault(e) ? e[DEFAULT_KEY] : e;
}
const aliases = {
  bg: "backgroundColor",
  m: "margin",
  mt: "marginTop",
  mr: "marginRight",
  mb: "marginBottom",
  ml: "marginLeft",
  mx: "marginX",
  my: "marginY",
  p: "padding",
  pt: "paddingTop",
  pr: "paddingRight",
  pb: "paddingBottom",
  pl: "paddingLeft",
  px: "paddingX",
  py: "paddingY"
}, multiples = {
  marginX: ["marginLeft", "marginRight"],
  marginY: ["marginTop", "marginBottom"],
  paddingX: ["paddingLeft", "paddingRight"],
  paddingY: ["paddingTop", "paddingBottom"],
  scrollMarginX: ["scrollMarginLeft", "scrollMarginRight"],
  scrollMarginY: ["scrollMarginTop", "scrollMarginBottom"],
  scrollPaddingX: ["scrollPaddingLeft", "scrollPaddingRight"],
  scrollPaddingY: ["scrollPaddingTop", "scrollPaddingBottom"],
  size: ["width", "height"]
}, scales = {
  color: "colors",
  background: "colors",
  backgroundColor: "colors",
  borderColor: "colors",
  caretColor: "colors",
  columnRuleColor: "colors",
  outlineColor: "colors",
  textDecorationColor: "colors",
  opacity: "opacities",
  transition: "transitions",
  transitionDuration: "durations",
  transitionTimingFunction: "easings",
  animationDuration: "durations",
  animationTimingFunction: "easings",
  margin: "space",
  marginTop: "space",
  marginRight: "space",
  marginBottom: "space",
  marginLeft: "space",
  marginX: "space",
  marginY: "space",
  marginBlock: "space",
  marginBlockEnd: "space",
  marginBlockStart: "space",
  marginInline: "space",
  marginInlineEnd: "space",
  marginInlineStart: "space",
  padding: "space",
  paddingTop: "space",
  paddingRight: "space",
  paddingBottom: "space",
  paddingLeft: "space",
  paddingX: "space",
  paddingY: "space",
  paddingBlock: "space",
  paddingBlockEnd: "space",
  paddingBlockStart: "space",
  paddingInline: "space",
  paddingInlineEnd: "space",
  paddingInlineStart: "space",
  scrollMargin: "space",
  scrollMarginTop: "space",
  scrollMarginRight: "space",
  scrollMarginBottom: "space",
  scrollMarginLeft: "space",
  scrollMarginX: "space",
  scrollMarginY: "space",
  scrollPadding: "space",
  scrollPaddingTop: "space",
  scrollPaddingRight: "space",
  scrollPaddingBottom: "space",
  scrollPaddingLeft: "space",
  scrollPaddingX: "space",
  scrollPaddingY: "space",
  inset: "space",
  insetBlock: "space",
  insetBlockEnd: "space",
  insetBlockStart: "space",
  insetInline: "space",
  insetInlineEnd: "space",
  insetInlineStart: "space",
  top: "space",
  right: "space",
  bottom: "space",
  left: "space",
  gridGap: "space",
  gridColumnGap: "space",
  gridRowGap: "space",
  gap: "space",
  columnGap: "space",
  rowGap: "space",
  fontFamily: "fonts",
  fontSize: "fontSizes",
  fontWeight: "fontWeights",
  lineHeight: "lineHeights",
  letterSpacing: "letterSpacings",
  border: "borders",
  borderTop: "borders",
  borderRight: "borders",
  borderBottom: "borders",
  borderLeft: "borders",
  borderWidth: "borderWidths",
  borderStyle: "borderStyles",
  borderRadius: "radii",
  borderTopRightRadius: "radii",
  borderTopLeftRadius: "radii",
  borderBottomRightRadius: "radii",
  borderBottomLeftRadius: "radii",
  borderTopWidth: "borderWidths",
  borderTopColor: "colors",
  borderTopStyle: "borderStyles",
  borderBottomWidth: "borderWidths",
  borderBottomColor: "colors",
  borderBottomStyle: "borderStyles",
  borderLeftWidth: "borderWidths",
  borderLeftColor: "colors",
  borderLeftStyle: "borderStyles",
  borderRightWidth: "borderWidths",
  borderRightColor: "colors",
  borderRightStyle: "borderStyles",
  borderBlock: "borders",
  borderBlockColor: "colors",
  borderBlockEnd: "borders",
  borderBlockEndColor: "colors",
  borderBlockEndStyle: "borderStyles",
  borderBlockEndWidth: "borderWidths",
  borderBlockStart: "borders",
  borderBlockStartColor: "colors",
  borderBlockStartStyle: "borderStyles",
  borderBlockStartWidth: "borderWidths",
  borderBlockStyle: "borderStyles",
  borderBlockWidth: "borderWidths",
  borderEndEndRadius: "radii",
  borderEndStartRadius: "radii",
  borderInline: "borders",
  borderInlineColor: "colors",
  borderInlineEnd: "borders",
  borderInlineEndColor: "colors",
  borderInlineEndStyle: "borderStyles",
  borderInlineEndWidth: "borderWidths",
  borderInlineStart: "borders",
  borderInlineStartColor: "colors",
  borderInlineStartStyle: "borderStyles",
  borderInlineStartWidth: "borderWidths",
  borderInlineStyle: "borderStyles",
  borderInlineWidth: "borderWidths",
  borderStartEndRadius: "radii",
  borderStartStartRadius: "radii",
  columnRuleWidth: "borderWidths",
  boxShadow: "shadows",
  textShadow: "shadows",
  zIndex: "zIndices",
  width: "sizes",
  minWidth: "sizes",
  maxWidth: "sizes",
  height: "sizes",
  minHeight: "sizes",
  maxHeight: "sizes",
  flexBasis: "sizes",
  size: "sizes",
  blockSize: "sizes",
  inlineSize: "sizes",
  maxBlockSize: "sizes",
  maxInlineSize: "sizes",
  minBlockSize: "sizes",
  minInlineSize: "sizes",
  columnWidth: "sizes",
  backgroundImage: "colors",
  // svg
  fill: "colors",
  stroke: "colors"
}, positiveOrNegative = (e, r) => {
  if (typeof r != "number" || r >= 0) {
    if (typeof r == "string" && r.startsWith("-")) {
      const o = r.substring(1), i = get(e, o, o);
      return typeof i == "number" ? i * -1 : `-${i}`;
    }
    return get(e, r, r);
  }
  const n = Math.abs(r), a = get(e, n, n);
  return typeof a == "string" ? "-" + a : Number(a) * -1;
}, transforms = [
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "marginX",
  "marginY",
  "top",
  "bottom",
  "left",
  "right",
  // Logical properties
  "marginBlock",
  "marginBlockEnd",
  "marginBlockStart",
  "marginInline",
  "marginInlineEnd",
  "marginInlineStart",
  "insetBlock",
  "insetBlockEnd",
  "insetBlockStart",
  "insetInline",
  "insetInlineEnd",
  "insetInlineStart"
].reduce((e, r) => ({
  ...e,
  [r]: positiveOrNegative
}), {}), responsive = (e) => (r) => {
  const n = {}, o = [null, ...get(r, "breakpoints", []).map((i) => i.includes("@media") ? i : `@media screen and (min-width: ${i})`)];
  for (const i in e) {
    const c = i, l = typeof e[c] == "function" ? e[c](r || {}) : e[c];
    if (!(l === !1 || l == null)) {
      if (!Array.isArray(l)) {
        n[c] = l;
        continue;
      }
      for (let p = 0; p < l.slice(0, o.length).length; p++) {
        const f = o[p];
        if (!f) {
          n[c] = l[p];
          continue;
        }
        n[f] = n[f] || {}, l[p] != null && (n[f][c] = l[p]);
      }
    }
  }
  return n;
}, _css = (e) => function() {
  let r = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
  const n = {
    // ...defaultTheme,
    ..."theme" in r ? r.theme : r
  }, a = typeof e == "function" ? e(n) : e, o = Array.isArray(a) ? merge.all(a.filter((l) => l && typeof l == "object")) : a, i = responsive(o || {})(n);
  let c = {};
  for (const l in i) {
    const p = i[l], f = typeof p == "function" ? p(n) : p;
    if (l === "variant") {
      const d = _css(get(n, f))(n);
      c = {
        ...c,
        ...d
      };
      continue;
    }
    if (f && typeof f == "object") {
      if (hasDefault(f)) {
        c[l] = f[DEFAULT_KEY];
        continue;
      }
      c[l] = _css(f)(n);
      continue;
    }
    const u = l in aliases ? aliases[l] : l, m = u in scales ? scales[u] : void 0, $ = m ? n?.[m] : get(n, u, {}), y = get(transforms, u, get)($, f, f);
    if (u in multiples) {
      const d = multiples[u];
      for (let h = 0; h < d.length; h++)
        c[d[h]] = y;
    } else
      c[u] = y;
  }
  return c;
}, css = (e) => function() {
  let r = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
  return _css(e)(r);
};
function _extends$3() {
  return _extends$3 = Object.assign ? Object.assign.bind() : function(e) {
    for (var r = 1; r < arguments.length; r++) {
      var n = arguments[r];
      for (var a in n) ({}).hasOwnProperty.call(n, a) && (e[a] = n[a]);
    }
    return e;
  }, _extends$3.apply(null, arguments);
}
function murmur2(e) {
  for (var r = 0, n, a = 0, o = e.length; o >= 4; ++a, o -= 4)
    n = e.charCodeAt(a) & 255 | (e.charCodeAt(++a) & 255) << 8 | (e.charCodeAt(++a) & 255) << 16 | (e.charCodeAt(++a) & 255) << 24, n = /* Math.imul(k, m): */
    (n & 65535) * 1540483477 + ((n >>> 16) * 59797 << 16), n ^= /* k >>> r: */
    n >>> 24, r = /* Math.imul(k, m): */
    (n & 65535) * 1540483477 + ((n >>> 16) * 59797 << 16) ^ /* Math.imul(h, m): */
    (r & 65535) * 1540483477 + ((r >>> 16) * 59797 << 16);
  switch (o) {
    case 3:
      r ^= (e.charCodeAt(a + 2) & 255) << 16;
    case 2:
      r ^= (e.charCodeAt(a + 1) & 255) << 8;
    case 1:
      r ^= e.charCodeAt(a) & 255, r = /* Math.imul(h, m): */
      (r & 65535) * 1540483477 + ((r >>> 16) * 59797 << 16);
  }
  return r ^= r >>> 13, r = /* Math.imul(h, m): */
  (r & 65535) * 1540483477 + ((r >>> 16) * 59797 << 16), ((r ^ r >>> 15) >>> 0).toString(36);
}
var unitlessKeys = {
  animationIterationCount: 1,
  aspectRatio: 1,
  borderImageOutset: 1,
  borderImageSlice: 1,
  borderImageWidth: 1,
  boxFlex: 1,
  boxFlexGroup: 1,
  boxOrdinalGroup: 1,
  columnCount: 1,
  columns: 1,
  flex: 1,
  flexGrow: 1,
  flexPositive: 1,
  flexShrink: 1,
  flexNegative: 1,
  flexOrder: 1,
  gridRow: 1,
  gridRowEnd: 1,
  gridRowSpan: 1,
  gridRowStart: 1,
  gridColumn: 1,
  gridColumnEnd: 1,
  gridColumnSpan: 1,
  gridColumnStart: 1,
  msGridRow: 1,
  msGridRowSpan: 1,
  msGridColumn: 1,
  msGridColumnSpan: 1,
  fontWeight: 1,
  lineHeight: 1,
  opacity: 1,
  order: 1,
  orphans: 1,
  scale: 1,
  tabSize: 1,
  widows: 1,
  zIndex: 1,
  zoom: 1,
  WebkitLineClamp: 1,
  // SVG-related properties
  fillOpacity: 1,
  floodOpacity: 1,
  stopOpacity: 1,
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeMiterlimit: 1,
  strokeOpacity: 1,
  strokeWidth: 1
};
function memoize$2(e) {
  var r = /* @__PURE__ */ Object.create(null);
  return function(n) {
    return r[n] === void 0 && (r[n] = e(n)), r[n];
  };
}
var hyphenateRegex = /[A-Z]|^ms/g, animationRegex = /_EMO_([^_]+?)_([^]*?)_EMO_/g, isCustomProperty = function(r) {
  return r.charCodeAt(1) === 45;
}, isProcessableValue = function(r) {
  return r != null && typeof r != "boolean";
}, processStyleName = /* @__PURE__ */ memoize$2(function(e) {
  return isCustomProperty(e) ? e : e.replace(hyphenateRegex, "-$&").toLowerCase();
}), processStyleValue = function(r, n) {
  switch (r) {
    case "animation":
    case "animationName":
      if (typeof n == "string")
        return n.replace(animationRegex, function(a, o, i) {
          return cursor = {
            name: o,
            styles: i,
            next: cursor
          }, o;
        });
  }
  return unitlessKeys[r] !== 1 && !isCustomProperty(r) && typeof n == "number" && n !== 0 ? n + "px" : n;
};
function handleInterpolation(e, r, n) {
  if (n == null)
    return "";
  var a = n;
  if (a.__emotion_styles !== void 0)
    return a;
  switch (typeof n) {
    case "boolean":
      return "";
    case "object": {
      var o = n;
      if (o.anim === 1)
        return cursor = {
          name: o.name,
          styles: o.styles,
          next: cursor
        }, o.name;
      var i = n;
      if (i.styles !== void 0) {
        var c = i.next;
        if (c !== void 0)
          for (; c !== void 0; )
            cursor = {
              name: c.name,
              styles: c.styles,
              next: cursor
            }, c = c.next;
        var l = i.styles + ";";
        return l;
      }
      return createStringFromObject(e, r, n);
    }
    case "function": {
      if (e !== void 0) {
        var p = cursor, f = n(e);
        return cursor = p, handleInterpolation(e, r, f);
      }
      break;
    }
  }
  var u = n;
  if (r == null)
    return u;
  var m = r[u];
  return m !== void 0 ? m : u;
}
function createStringFromObject(e, r, n) {
  var a = "";
  if (Array.isArray(n))
    for (var o = 0; o < n.length; o++)
      a += handleInterpolation(e, r, n[o]) + ";";
  else
    for (var i in n) {
      var c = n[i];
      if (typeof c != "object") {
        var l = c;
        r != null && r[l] !== void 0 ? a += i + "{" + r[l] + "}" : isProcessableValue(l) && (a += processStyleName(i) + ":" + processStyleValue(i, l) + ";");
      } else if (Array.isArray(c) && typeof c[0] == "string" && (r == null || r[c[0]] === void 0))
        for (var p = 0; p < c.length; p++)
          isProcessableValue(c[p]) && (a += processStyleName(i) + ":" + processStyleValue(i, c[p]) + ";");
      else {
        var f = handleInterpolation(e, r, c);
        switch (i) {
          case "animation":
          case "animationName": {
            a += processStyleName(i) + ":" + f + ";";
            break;
          }
          default:
            a += i + "{" + f + "}";
        }
      }
    }
  return a;
}
var labelPattern = /label:\s*([^\s;{]+)\s*(;|$)/g, cursor;
function serializeStyles(e, r, n) {
  if (e.length === 1 && typeof e[0] == "object" && e[0] !== null && e[0].styles !== void 0)
    return e[0];
  var a = !0, o = "";
  cursor = void 0;
  var i = e[0];
  if (i == null || i.raw === void 0)
    a = !1, o += handleInterpolation(n, r, i);
  else {
    var c = i;
    o += c[0];
  }
  for (var l = 1; l < e.length; l++)
    if (o += handleInterpolation(n, r, e[l]), a) {
      var p = i;
      o += p[l];
    }
  labelPattern.lastIndex = 0;
  for (var f = "", u; (u = labelPattern.exec(o)) !== null; )
    f += "-" + u[1];
  var m = murmur2(o) + f;
  return {
    name: m,
    styles: o,
    next: cursor
  };
}
var syncFallback = function(r) {
  return r();
}, useInsertionEffect = React && false ? React && false : !1, useInsertionEffectAlwaysWithSyncFallback = useInsertionEffect || syncFallback, isBrowser = !0;
function getRegisteredStyles(e, r, n) {
  var a = "";
  return n.split(" ").forEach(function(o) {
    e[o] !== void 0 ? r.push(e[o] + ";") : o && (a += o + " ");
  }), a;
}
var registerStyles = function(r, n, a) {
  var o = r.key + "-" + n.name;
  // we only need to add the styles to the registered cache if the
  // class name could be used further down
  // the tree but if it's a string tag, we know it won't
  // so we don't have to add it to registered cache.
  // this improves memory usage since we can avoid storing the whole style string
  (a === !1 || // we need to always store it if we're in compat mode and
  // in node since emotion-server relies on whether a style is in
  // the registered cache to know whether a style is global or not
  // also, note that this check will be dead code eliminated in the browser
  isBrowser === !1) && r.registered[o] === void 0 && (r.registered[o] = n.styles);
}, insertStyles = function(r, n, a) {
  registerStyles(r, n, a);
  var o = r.key + "-" + n.name;
  if (r.inserted[n.name] === void 0) {
    var i = n;
    do
      r.insert(n === i ? "." + o : "", i, r.sheet, !0), i = i.next;
    while (i !== void 0);
  }
}, testOmitPropsOnStringTag = isPropValid, testOmitPropsOnComponent = function(r) {
  return r !== "theme";
}, getDefaultShouldForwardProp = function(r) {
  return typeof r == "string" && // 96 is one less than the char code
  // for "a" so this is checking that
  // it's a lowercase character
  r.charCodeAt(0) > 96 ? testOmitPropsOnStringTag : testOmitPropsOnComponent;
}, composeShouldForwardProps = function(r, n, a) {
  var o;
  if (n) {
    var i = n.shouldForwardProp;
    o = r.__emotion_forwardProp && i ? function(c) {
      return r.__emotion_forwardProp(c) && i(c);
    } : i;
  }
  return typeof o != "function" && a && (o = r.__emotion_forwardProp), o;
}, Insertion = function(r) {
  var n = r.cache, a = r.serialized, o = r.isStringTag;
  return registerStyles(n, a, o), useInsertionEffectAlwaysWithSyncFallback(function() {
    return insertStyles(n, a, o);
  }), null;
}, createStyled = function e(r, n) {
  var a = r.__emotion_real === r, o = a && r.__emotion_base || r, i, c;
  n !== void 0 && (i = n.label, c = n.target);
  var l = composeShouldForwardProps(r, n, a), p = l || getDefaultShouldForwardProp(o), f = !p("as");
  return function() {
    var u = arguments, m = a && r.__emotion_styles !== void 0 ? r.__emotion_styles.slice(0) : [];
    if (i !== void 0 && m.push("label:" + i + ";"), u[0] == null || u[0].raw === void 0)
      m.push.apply(m, u);
    else {
      var $ = u[0];
      m.push($[0]);
      for (var v = u.length, y = 1; y < v; y++)
        m.push(u[y], $[y]);
    }
    var d = withEmotionCache(function(h, b, _) {
      var R = f && h.as || o, w = "", g = [], O = h;
      if (h.theme == null) {
        O = {};
        for (var I in h)
          O[I] = h[I];
        O.theme = React.useContext(ThemeContext);
      }
      typeof h.className == "string" ? w = getRegisteredStyles(b.registered, g, h.className) : h.className != null && (w = h.className + " ");
      var D = serializeStyles(m.concat(g), b.registered, O);
      w += b.key + "-" + D.name, c !== void 0 && (w += " " + c);
      var L = f && l === void 0 ? getDefaultShouldForwardProp(R) : p, j = {};
      for (var M in h)
        f && M === "as" || L(M) && (j[M] = h[M]);
      return j.className = w, _ && (j.ref = _), /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Insertion, {
        cache: b,
        serialized: D,
        isStringTag: typeof R == "string"
      }), /* @__PURE__ */ React.createElement(R, j));
    });
    return d.displayName = i !== void 0 ? i : "Styled(" + (typeof o == "string" ? o : o.displayName || o.name || "Component") + ")", d.defaultProps = r.defaultProps, d.__emotion_real = d, d.__emotion_base = o, d.__emotion_styles = m, d.__emotion_forwardProp = l, Object.defineProperty(d, "toString", {
      value: function() {
        return "." + c;
      }
    }), d.withComponent = function(h, b) {
      var _ = e(h, _extends$3({}, n, b, {
        shouldForwardProp: composeShouldForwardProps(d, b, !0)
      }));
      return _.apply(void 0, m);
    }, d;
  };
}, src = { exports: {} };
function memoize(e, r) {
  var n = r && r.cache ? r.cache : cacheDefault, a = r && r.serializer ? r.serializer : serializerDefault, o = r && r.strategy ? r.strategy : strategyDefault;
  return o(e, {
    cache: n,
    serializer: a
  });
}
function isPrimitive(e) {
  return e == null || typeof e == "number" || typeof e == "boolean";
}
function monadic(e, r, n, a) {
  var o = isPrimitive(a) ? a : n(a), i = r.get(o);
  return typeof i > "u" && (i = e.call(this, a), r.set(o, i)), i;
}
function variadic(e, r, n) {
  var a = Array.prototype.slice.call(arguments, 3), o = n(a), i = r.get(o);
  return typeof i > "u" && (i = e.apply(this, a), r.set(o, i)), i;
}
function assemble(e, r, n, a, o) {
  return n.bind(
    r,
    e,
    a,
    o
  );
}
function strategyDefault(e, r) {
  var n = e.length === 1 ? monadic : variadic;
  return assemble(
    e,
    this,
    n,
    r.cache.create(),
    r.serializer
  );
}
function strategyVariadic(e, r) {
  var n = variadic;
  return assemble(
    e,
    this,
    n,
    r.cache.create(),
    r.serializer
  );
}
function strategyMonadic(e, r) {
  var n = monadic;
  return assemble(
    e,
    this,
    n,
    r.cache.create(),
    r.serializer
  );
}
function serializerDefault() {
  return JSON.stringify(arguments);
}
function ObjectWithoutPrototypeCache() {
  this.cache = /* @__PURE__ */ Object.create(null);
}
ObjectWithoutPrototypeCache.prototype.has = function(e) {
  return e in this.cache;
};
ObjectWithoutPrototypeCache.prototype.get = function(e) {
  return this.cache[e];
};
ObjectWithoutPrototypeCache.prototype.set = function(e, r) {
  this.cache[e] = r;
};
var cacheDefault = {
  create: function() {
    return new ObjectWithoutPrototypeCache();
  }
};
src.exports = memoize;
src.exports.strategies = {
  variadic: strategyVariadic,
  monadic: strategyMonadic
};
var srcExports = src.exports;
const memoize$1 = /* @__PURE__ */ getDefaultExportFromCjs(srcExports);
class t {
  constructor(r = 0, n = 0) {
    this.first = null, this.items = /* @__PURE__ */ Object.create(null), this.last = null, this.max = r, this.size = 0, this.ttl = n;
  }
  has(r) {
    return r in this.items;
  }
  clear() {
    return this.first = null, this.items = /* @__PURE__ */ Object.create(null), this.last = null, this.size = 0, this;
  }
  delete(r) {
    if (this.has(r)) {
      const n = this.items[r];
      delete this.items[r], this.size--, n.prev !== null && (n.prev.next = n.next), n.next !== null && (n.next.prev = n.prev), this.first === n && (this.first = n.next), this.last === n && (this.last = n.prev);
    }
    return this;
  }
  evict(r = !1) {
    if (r || this.size > 0) {
      const n = this.first;
      delete this.items[n.key], this.size--, this.size === 0 ? (this.first = null, this.last = null) : (this.first = n.next, this.first.prev = null);
    }
    return this;
  }
  get(r) {
    let n;
    if (this.has(r)) {
      const a = this.items[r];
      this.ttl > 0 && a.expiry <= (/* @__PURE__ */ new Date()).getTime() ? this.delete(r) : (n = a.value, this.set(r, n, !0));
    }
    return n;
  }
  keys() {
    return Object.keys(this.items);
  }
  set(r, n, a = !1) {
    let o;
    if (a || this.has(r)) {
      if (o = this.items[r], o.value = n, this.last !== o) {
        const i = this.last, c = o.next, l = o.prev;
        this.first === o && (this.first = o.next), o.next = null, o.prev = this.last, i.next = o, l !== null && (l.next = c), c !== null && (c.prev = l);
      }
    } else this.max > 0 && this.size === this.max && this.evict(!0), o = this.items[r] = { expiry: this.ttl > 0 ? (/* @__PURE__ */ new Date()).getTime() + this.ttl : this.ttl, key: r, prev: this.last, next: null, value: n }, ++this.size == 1 ? this.first = o : this.last.next = o;
    return this.last = o, this;
  }
}
function s(e = 1e3, r = 0) {
  if (isNaN(e) || e < 0) throw new TypeError("Invalid max value");
  if (isNaN(r) || r < 0) throw new TypeError("Invalid ttl value");
  return new t(e, r);
}
const hasFunctionStyles = (e) => {
  for (const r in e)
    if (Object.prototype.hasOwnProperty.call(e, r)) {
      const n = e[r];
      if (typeof n == "function" || n && typeof n == "object" && hasFunctionStyles(n))
        return !0;
    }
  return !1;
}, shouldForwardProp = (e) => {
  const r = ["as", "sx"];
  return !(/* @__PURE__ */ new Set([...r])).has(e);
}, warn = process.env.NODE_ENV !== "production" && console ? console.warn.bind(console) : function() {
};
function _extends$2() {
  return _extends$2 = Object.assign ? Object.assign.bind() : function(e) {
    for (var r = 1; r < arguments.length; r++) {
      var n = arguments[r];
      for (var a in n) ({}).hasOwnProperty.call(n, a) && (e[a] = n[a]);
    }
    return e;
  }, _extends$2.apply(null, arguments);
}
let themeCount = 1;
const themeMap = /* @__PURE__ */ new WeakMap(), registry = new FinalizationRegistry(() => {
  themeCount = Math.max(0, themeCount - 1);
}), sxCache = new s(), createStyleSerializer = (e) => (n) => {
  let {
    theme: a,
    sx: o,
    children: i,
    ...c
  } = n;
  process.env.NODE_ENV !== "production" && Object.keys(a).length === 0 && warn("No theme found. Please wrap your components in an IndeedThemeProvider for proper operation. See https://experience.indeed.design/fundamentals/ids/getting-started for more information");
  let l = themeMap.get(a);
  return l === void 0 && (l = themeCount, themeCount = themeCount + 1, themeCount > 50 && warn(`There are ${themeCount} themes in the theme cache. You may experience performance issues due to the large cache size. Please ensure that you are providing the same theme object (in memory) to IndeedThemeProvider. See https://experience.indeed.design/fundamentals/ids/ifl-performance-guidelines for more information.`), themeMap.set(a, l), registry.register(a)), `${(e ? e.map((f) => c[f] ?? "0").join("-") : Object.values(c).join("-")) || "0"}-${l}`;
}, createSxSerializer = () => (r) => {
  const [n, a] = r;
  process.env.NODE_ENV !== "production" && Object.keys(a).length === 0 && warn("No theme found. Please wrap your components in an IndeedThemeProvider for proper operation. See https://experience.indeed.design/fundamentals/ids/getting-started for more information");
  let o = themeMap.get(a);
  return o === void 0 && (o = themeCount, themeCount = themeCount + 1, themeCount > 50 && warn(`There are ${themeCount} themes in the theme cache. You may experience performance issues due to the large cache size. Please ensure that you are providing the same theme object (in memory) to IndeedThemeProvider. See https://experience.indeed.design/fundamentals/ids/ifl-performance-guidelines for more information.`), themeMap.set(a, o), registry.register(a)), `${JSON.stringify(n)}-${o}`;
}, memoizedCss = memoize$1((e, r) => css(e)(r), {
  serializer: createSxSerializer(),
  cache: {
    create() {
      return sxCache;
    }
  }
}), sxProp = (e) => {
  const {
    sx: r,
    theme: n
  } = e;
  return r ? hasFunctionStyles(r) ? css(r)(n) : memoizedCss(r, n) : null;
}, themed = (e, r) => {
  const {
    styleProps: n,
    ...a
  } = r ?? {};
  let o;
  a && (o = a.shouldForwardProp);
  const i = o ? (l) => (
    // shouldForwardProp is composed of option shouldForwardProp & default shouldForwardProp
    o(l) && shouldForwardProp(l)
  ) : shouldForwardProp;
  a.shouldForwardProp = i;
  const c = createStyled(e, process.env.NODE_ENV === "production" ? _extends$2({}, {
    target: "eac13zx0"
  }, a) : _extends$2({}, {
    target: "eac13zx0",
    label: "factory"
  }, a));
  return function() {
    for (var l = arguments.length, p = new Array(l), f = 0; f < l; f++)
      p[f] = arguments[f];
    const u = n;
    if (p.length === 1 && typeof p[0] == "object" || u === !1)
      return c(...p, sxProp);
    const m = memoize$1((v) => p.map((y) => typeof y == "function" ? css(y(v))(v.theme) : y), {
      serializer: createStyleSerializer(u),
      cache: {
        create() {
          return s(1e3);
        }
      }
    }), $ = c(m, sxProp);
    return $.__themedCacheProps = u, $.__withComponent = $.withComponent, $.withComponent = (v, y) => v.__themedCacheProps ? themed(v, {
      ...r,
      ...y,
      styleProps: [...u || [], ...v.__themedCacheProps || []]
    })(...p) : $.__withComponent(v, y), $;
  };
}, IconProviderContext = /* @__PURE__ */ React__default.createContext({});
IconProviderContext.Provider;
const useIconProviderContext = () => React__default.useContext(IconProviderContext);
function _extends$1() {
  return _extends$1 = Object.assign ? Object.assign.bind() : function(e) {
    for (var r = 1; r < arguments.length; r++) {
      var n = arguments[r];
      for (var a in n) ({}).hasOwnProperty.call(n, a) && (e[a] = n[a]);
    }
    return e;
  }, _extends$1.apply(null, arguments);
}
const svgSizes = {
  xs: "1.125rem",
  sm: "1.25rem",
  md: "1.5rem"
}, baseStyles = (e) => {
  let {
    color: r = "inherit",
    size: n = "md"
  } = e;
  return {
    inlineSize: svgSizes[n],
    blockSize: svgSizes[n],
    color: r
  };
}, rejectedAttributes = /* @__PURE__ */ new Set(["color", "size"]), StyledSVG = themed("svg", {
  styleProps: ["color", "size"],
  shouldForwardProp: (e) => isPropValid(e) && !rejectedAttributes.has(e)
})(baseStyles), Icon = /* @__PURE__ */ forwardRef((e, r) => {
  let {
    children: n,
    fill: a = "currentColor",
    paths: o,
    size: i = "md",
    variant: c,
    title: l,
    useDirection: p = !0,
    ...f
  } = e;
  const u = "0 0 24 24", m = useTheme(), {
    variant: $
  } = useIconProviderContext(), v = c || $ || "filled", y = p && m.direction === "rtl" && o && (o[`${v}${m.direction}`] || o[`${i}${m.direction}`]) ? m.direction : "", d = `${v}${y}`;
  return /* @__PURE__ */ React__default.createElement(StyledSVG, _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    focusable: "false",
    role: "img",
    ref: r,
    fill: a,
    size: i,
    viewBox: u
  }, f), l && /* @__PURE__ */ React__default.createElement("title", null, l), o ? o[d] : n);
});
Icon.displayName = "Icon";
const ArrowLeft = /* @__PURE__ */ forwardRef((e, r) => {
  let {
    fill: n = "currentColor",
    size: a = "md",
    variant: o,
    ...i
  } = e;
  const c = {
    filled: /* @__PURE__ */ React__default.createElement("path", {
      d: "M6.1418 13.1383h14.924c.3192 0 .5876-.1097.8053-.3292.2175-.2197.3262-.4891.3262-.8083 0-.3191-.1097-.5886-.3292-.8082-.2197-.2195-.4891-.3293-.8083-.3293H6.1418l3.6658-3.6727c.2153-.2114.3262-.4744.3325-.7893.0063-.3148-.1045-.5841-.3325-.808-.2112-.2113-.4773-.316-.7985-.314-.3212.002-.5874.1086-.7988.3198l-5.606 5.606c-.1118.112-.1925.2363-.2422.373a1.2439 1.2439 0 00-.0745.4287c0 .1494.0248.2913.0745.4258.0497.1346.1304.258.2423.37l5.606 5.6117c.2113.2114.4734.318.7862.32.3128.002.5832-.1046.811-.32.228-.2278.342-.4971.342-.808 0-.3108-.114-.5802-.342-.8082l-3.6658-3.6598z"
    }),
    filledrtl: /* @__PURE__ */ React__default.createElement("path", {
      d: "M18.3431 13.1383H3.4191c-.3192 0-.5876-.1097-.8053-.3292-.2174-.2197-.3262-.4891-.3262-.8083 0-.3191.1098-.5886.3293-.8082.2196-.2195.489-.3293.8082-.3293h14.918l-3.6658-3.6727c-.2153-.2114-.3261-.4744-.3325-.7893-.0063-.3148.1045-.5841.3325-.808.2112-.2113.4774-.316.7985-.314.3212.002.5875.1086.7988.3198l5.606 5.606c.1118.112.1926.2363.2422.373.0497.1365.0745.2794.0745.4287 0 .1494-.0248.2913-.0745.4258-.0496.1346-.1304.258-.2422.37l-5.606 5.6117c-.2113.2114-.4734.318-.7863.32-.3128.002-.5831-.1046-.811-.32-.228-.2278-.342-.4971-.342-.808 0-.3108.114-.5802.342-.8082l3.6658-3.6598z"
    }),
    stroke: /* @__PURE__ */ React__default.createElement("path", {
      d: "M6.1418 13.1383h14.924c.3192 0 .5876-.1097.8053-.3292.2175-.2197.3262-.4891.3262-.8083 0-.3191-.1097-.5886-.3292-.8082-.2197-.2195-.4891-.3293-.8083-.3293H6.1418l3.6658-3.6727c.2153-.2114.3262-.4744.3325-.7893.0063-.3148-.1045-.5841-.3325-.808-.2112-.2113-.4773-.316-.7985-.314-.3212.002-.5874.1086-.7988.3198l-5.606 5.606c-.1118.112-.1925.2363-.2422.373a1.2439 1.2439 0 00-.0745.4287c0 .1494.0248.2913.0745.4258.0497.1346.1304.258.2423.37l5.606 5.6117c.2113.2114.4734.318.7862.32.3128.002.5832-.1046.811-.32.228-.2278.342-.4971.342-.808 0-.3108-.114-.5802-.342-.8082l-3.6658-3.6598z"
    }),
    strokertl: /* @__PURE__ */ React__default.createElement("path", {
      d: "M18.3431 13.1383H3.4191c-.3192 0-.5876-.1097-.8053-.3292-.2174-.2197-.3262-.4891-.3262-.8083 0-.3191.1098-.5886.3293-.8082.2196-.2195.489-.3293.8082-.3293h14.918l-3.6658-3.6727c-.2153-.2114-.3261-.4744-.3325-.7893-.0063-.3148.1045-.5841.3325-.808.2112-.2113.4774-.316.7985-.314.3212.002.5875.1086.7988.3198l5.606 5.606c.1118.112.1926.2363.2422.373.0497.1365.0745.2794.0745.4287 0 .1494-.0248.2913-.0745.4258-.0496.1346-.1304.258-.2422.37l-5.606 5.6117c-.2113.2114-.4734.318-.7863.32-.3128.002-.5831-.1046-.811-.32-.228-.2278-.342-.4971-.342-.808 0-.3108.114-.5802.342-.8082l3.6658-3.6598z"
    })
  };
  return /* @__PURE__ */ React__default.createElement(Icon, _extends$1({
    ref: r,
    fill: n,
    paths: c,
    size: a,
    variant: o
  }, i));
});
ArrowLeft.displayName = "ArrowLeftIcon";
const ArrowRight = /* @__PURE__ */ forwardRef((e, r) => {
  let {
    fill: n = "currentColor",
    size: a = "md",
    variant: o,
    ...i
  } = e;
  const c = {
    filled: /* @__PURE__ */ React__default.createElement("path", {
      d: "M17.864 13.1383H2.9402c-.3191 0-.5886-.1097-.8082-.3292-.2195-.2197-.3293-.4891-.3293-.8083 0-.3191.1098-.5886.3293-.8082.2196-.2195.489-.3293.8082-.3293h14.918l-3.66-3.6727c-.2151-.2114-.3259-.4744-.3322-.7893-.0063-.3148.1044-.5841.3322-.808.2114-.2113.4776-.316.7988-.314.3212.002.5874.1086.7987.3198l5.606 5.606c.1119.112.1926.2363.2423.373.0497.1365.0745.2794.0745.4287 0 .1494-.0248.2913-.0745.4258-.0497.1346-.1304.258-.2423.37l-5.606 5.6117c-.2113.2114-.4734.318-.7862.32-.3128.002-.5833-.1046-.8113-.32-.2278-.2278-.3417-.4971-.3417-.808 0-.3108.1139-.5802.3417-.8082l3.6658-3.6598z"
    }),
    filledrtl: /* @__PURE__ */ React__default.createElement("path", {
      d: "M5.6572 13.1383H20.581c.3192 0 .5886-.1097.8082-.3292.2195-.2197.3293-.4891.3293-.8083 0-.3191-.1098-.5886-.3293-.8082-.2196-.2195-.489-.3293-.8082-.3293H5.663l3.66-3.6727c.2151-.2114.3259-.4744.3322-.7893.0064-.3148-.1044-.5841-.3322-.808-.2114-.2113-.4776-.316-.7988-.314-.3211.002-.5874.1086-.7987.3198l-5.606 5.606c-.1119.112-.1926.2363-.2423.373a1.2441 1.2441 0 00-.0745.4287c0 .1494.0249.2913.0745.4258.0497.1346.1304.258.2423.37l5.606 5.6117c.2113.2114.4734.318.7862.32.3129.002.5833-.1046.8113-.32.2278-.2278.3417-.4971.3417-.808 0-.3108-.1139-.5802-.3417-.8082l-3.6658-3.6598z"
    }),
    stroke: /* @__PURE__ */ React__default.createElement("path", {
      d: "M17.864 13.1383H2.9402c-.3191 0-.5886-.1097-.8082-.3292-.2195-.2197-.3293-.4891-.3293-.8083 0-.3191.1098-.5886.3293-.8082.2196-.2195.489-.3293.8082-.3293h14.918l-3.66-3.6727c-.2151-.2114-.3259-.4744-.3322-.7893-.0063-.3148.1044-.5841.3322-.808.2114-.2113.4776-.316.7988-.314.3212.002.5874.1086.7987.3198l5.606 5.606c.1119.112.1926.2363.2423.373.0497.1365.0745.2794.0745.4287 0 .1494-.0248.2913-.0745.4258-.0497.1346-.1304.258-.2423.37l-5.606 5.6117c-.2113.2114-.4734.318-.7862.32-.3128.002-.5833-.1046-.8113-.32-.2278-.2278-.3417-.4971-.3417-.808 0-.3108.1139-.5802.3417-.8082l3.6658-3.6598z"
    }),
    strokertl: /* @__PURE__ */ React__default.createElement("path", {
      d: "M5.6572 13.1383H20.581c.3192 0 .5886-.1097.8082-.3292.2195-.2197.3293-.4891.3293-.8083 0-.3191-.1098-.5886-.3293-.8082-.2196-.2195-.489-.3293-.8082-.3293H5.663l3.66-3.6727c.2151-.2114.3259-.4744.3322-.7893.0064-.3148-.1044-.5841-.3322-.808-.2114-.2113-.4776-.316-.7988-.314-.3211.002-.5874.1086-.7987.3198l-5.606 5.606c-.1119.112-.1926.2363-.2423.373a1.2441 1.2441 0 00-.0745.4287c0 .1494.0249.2913.0745.4258.0497.1346.1304.258.2423.37l5.606 5.6117c.2113.2114.4734.318.7862.32.3129.002.5833-.1046.8113-.32.2278-.2278.3417-.4971.3417-.808 0-.3108-.1139-.5802-.3417-.8082l-3.6658-3.6598z"
    })
  };
  return /* @__PURE__ */ React__default.createElement(Icon, _extends$1({
    ref: r,
    fill: n,
    paths: c,
    size: a,
    variant: o
  }, i));
});
ArrowRight.displayName = "ArrowRightIcon";
const $f0a04ccd8dbdd83b$export$e5c5a5f917a5871c = typeof document < "u" ? React__default.useLayoutEffect : () => {
};
function $8ae05eaa5c114e9c$export$7f54fc3180508a52(e) {
  const r = useRef(null);
  return $f0a04ccd8dbdd83b$export$e5c5a5f917a5871c(() => {
    r.current = e;
  }, [
    e
  ]), useCallback((...n) => {
    const a = r.current;
    return a?.(...n);
  }, []);
}
const $431fbd86ca7dc216$export$b204af158042fbac = (e) => {
  var r;
  return (r = e?.ownerDocument) !== null && r !== void 0 ? r : document;
}, $431fbd86ca7dc216$export$f21a1ffae260145a = (e) => e && "window" in e && e.window === e ? e : $431fbd86ca7dc216$export$b204af158042fbac(e).defaultView || window;
function $c87311424ea30a05$var$testUserAgent(e) {
  var r;
  return typeof window > "u" || window.navigator == null ? !1 : ((r = window.navigator.userAgentData) === null || r === void 0 ? void 0 : r.brands.some((n) => e.test(n.brand))) || e.test(window.navigator.userAgent);
}
function $c87311424ea30a05$var$testPlatform(e) {
  var r;
  return typeof window < "u" && window.navigator != null ? e.test(((r = window.navigator.userAgentData) === null || r === void 0 ? void 0 : r.platform) || window.navigator.platform) : !1;
}
function $c87311424ea30a05$export$9ac100e40613ea10() {
  return $c87311424ea30a05$var$testPlatform(/^Mac/i);
}
function $c87311424ea30a05$export$186c6964ca17d99() {
  return $c87311424ea30a05$var$testPlatform(/^iPhone/i);
}
function $c87311424ea30a05$export$7bef049ce92e4224() {
  return $c87311424ea30a05$var$testPlatform(/^iPad/i) || // iPadOS 13 lies and says it's a Mac, but we can distinguish by detecting touch support.
  $c87311424ea30a05$export$9ac100e40613ea10() && navigator.maxTouchPoints > 1;
}
function $c87311424ea30a05$export$fedb369cb70207f1() {
  return $c87311424ea30a05$export$186c6964ca17d99() || $c87311424ea30a05$export$7bef049ce92e4224();
}
function $c87311424ea30a05$export$a11b0059900ceec8() {
  return $c87311424ea30a05$var$testUserAgent(/Android/i);
}
let $bbed8b41f857bcc0$var$transitionsByElement = /* @__PURE__ */ new Map(), $bbed8b41f857bcc0$var$transitionCallbacks = /* @__PURE__ */ new Set();
function $bbed8b41f857bcc0$var$setupGlobalEvents() {
  if (typeof window > "u") return;
  function e(a) {
    return "propertyName" in a;
  }
  let r = (a) => {
    if (!e(a) || !a.target) return;
    let o = $bbed8b41f857bcc0$var$transitionsByElement.get(a.target);
    o || (o = /* @__PURE__ */ new Set(), $bbed8b41f857bcc0$var$transitionsByElement.set(a.target, o), a.target.addEventListener("transitioncancel", n, {
      once: !0
    })), o.add(a.propertyName);
  }, n = (a) => {
    if (!e(a) || !a.target) return;
    let o = $bbed8b41f857bcc0$var$transitionsByElement.get(a.target);
    if (o && (o.delete(a.propertyName), o.size === 0 && (a.target.removeEventListener("transitioncancel", n), $bbed8b41f857bcc0$var$transitionsByElement.delete(a.target)), $bbed8b41f857bcc0$var$transitionsByElement.size === 0)) {
      for (let i of $bbed8b41f857bcc0$var$transitionCallbacks) i();
      $bbed8b41f857bcc0$var$transitionCallbacks.clear();
    }
  };
  document.body.addEventListener("transitionrun", r), document.body.addEventListener("transitionend", n);
}
typeof document < "u" && (document.readyState !== "loading" ? $bbed8b41f857bcc0$var$setupGlobalEvents() : document.addEventListener("DOMContentLoaded", $bbed8b41f857bcc0$var$setupGlobalEvents));
function $bbed8b41f857bcc0$export$24490316f764c430(e) {
  requestAnimationFrame(() => {
    $bbed8b41f857bcc0$var$transitionsByElement.size === 0 ? e() : $bbed8b41f857bcc0$var$transitionCallbacks.add(e);
  });
}
function $03deb23ff14920c4$export$4eaf04e54aa8eed6() {
  let e = useRef(/* @__PURE__ */ new Map()), r = useCallback((o, i, c, l) => {
    let p = l?.once ? (...f) => {
      e.current.delete(c), c(...f);
    } : c;
    e.current.set(c, {
      type: i,
      eventTarget: o,
      fn: p,
      options: l
    }), o.addEventListener(i, c, l);
  }, []), n = useCallback((o, i, c, l) => {
    var p;
    let f = ((p = e.current.get(c)) === null || p === void 0 ? void 0 : p.fn) || c;
    o.removeEventListener(i, f, l), e.current.delete(c);
  }, []), a = useCallback(() => {
    e.current.forEach((o, i) => {
      n(o.eventTarget, o.type, i, o.options);
    });
  }, [
    n
  ]);
  return useEffect(() => a, [
    a
  ]), {
    addGlobalListener: r,
    removeGlobalListener: n,
    removeAllGlobalListeners: a
  };
}
function $6a7db85432448f7f$export$60278871457622de(e) {
  return e.mozInputSource === 0 && e.isTrusted ? !0 : $c87311424ea30a05$export$a11b0059900ceec8() && e.pointerType ? e.type === "click" && e.buttons === 1 : e.detail === 0 && !e.pointerType;
}
let $14c0b72509d70225$var$state = "default", $14c0b72509d70225$var$savedUserSelect = "", $14c0b72509d70225$var$modifiedElementMap = /* @__PURE__ */ new WeakMap();
function $14c0b72509d70225$export$16a4697467175487(e) {
  if ($c87311424ea30a05$export$fedb369cb70207f1()) {
    if ($14c0b72509d70225$var$state === "default") {
      const r = $431fbd86ca7dc216$export$b204af158042fbac(e);
      $14c0b72509d70225$var$savedUserSelect = r.documentElement.style.webkitUserSelect, r.documentElement.style.webkitUserSelect = "none";
    }
    $14c0b72509d70225$var$state = "disabled";
  } else (e instanceof HTMLElement || e instanceof SVGElement) && ($14c0b72509d70225$var$modifiedElementMap.set(e, e.style.userSelect), e.style.userSelect = "none");
}
function $14c0b72509d70225$export$b0d6fa1ab32e3295(e) {
  if ($c87311424ea30a05$export$fedb369cb70207f1()) {
    if ($14c0b72509d70225$var$state !== "disabled") return;
    $14c0b72509d70225$var$state = "restoring", setTimeout(() => {
      $bbed8b41f857bcc0$export$24490316f764c430(() => {
        if ($14c0b72509d70225$var$state === "restoring") {
          const r = $431fbd86ca7dc216$export$b204af158042fbac(e);
          r.documentElement.style.webkitUserSelect === "none" && (r.documentElement.style.webkitUserSelect = $14c0b72509d70225$var$savedUserSelect || ""), $14c0b72509d70225$var$savedUserSelect = "", $14c0b72509d70225$var$state = "default";
        }
      });
    }, 300);
  } else if ((e instanceof HTMLElement || e instanceof SVGElement) && e && $14c0b72509d70225$var$modifiedElementMap.has(e)) {
    let r = $14c0b72509d70225$var$modifiedElementMap.get(e);
    e.style.userSelect === "none" && (e.style.userSelect = r), e.getAttribute("style") === "" && e.removeAttribute("style"), $14c0b72509d70225$var$modifiedElementMap.delete(e);
  }
}
const $ae1eeba8b9eafd08$export$5165eccb35aaadb5 = React__default.createContext({
  register: () => {
  }
});
$ae1eeba8b9eafd08$export$5165eccb35aaadb5.displayName = "PressResponderContext";
let $507fabe10e71c6fb$var$changeHandlers = /* @__PURE__ */ new Set(), $507fabe10e71c6fb$export$d90243b58daecda7 = /* @__PURE__ */ new Map(), $507fabe10e71c6fb$var$hasEventBeforeFocus = !1, $507fabe10e71c6fb$var$hasBlurredWindowRecently = !1;
function $507fabe10e71c6fb$var$triggerChangeHandlers(e, r) {
  for (let n of $507fabe10e71c6fb$var$changeHandlers) n(e, r);
}
function $507fabe10e71c6fb$var$isValidKey(e) {
  return !(e.metaKey || !$c87311424ea30a05$export$9ac100e40613ea10() && e.altKey || e.ctrlKey || e.key === "Control" || e.key === "Shift" || e.key === "Meta");
}
function $507fabe10e71c6fb$var$handleKeyboardEvent(e) {
  $507fabe10e71c6fb$var$hasEventBeforeFocus = !0, $507fabe10e71c6fb$var$isValidKey(e) && $507fabe10e71c6fb$var$triggerChangeHandlers("keyboard", e);
}
function $507fabe10e71c6fb$var$handlePointerEvent(e) {
  (e.type === "mousedown" || e.type === "pointerdown") && ($507fabe10e71c6fb$var$hasEventBeforeFocus = !0, $507fabe10e71c6fb$var$triggerChangeHandlers("pointer", e));
}
function $507fabe10e71c6fb$var$handleClickEvent(e) {
  $6a7db85432448f7f$export$60278871457622de(e) && ($507fabe10e71c6fb$var$hasEventBeforeFocus = !0);
}
function $507fabe10e71c6fb$var$handleFocusEvent(e) {
  e.target === window || e.target === document || (!$507fabe10e71c6fb$var$hasEventBeforeFocus && !$507fabe10e71c6fb$var$hasBlurredWindowRecently && $507fabe10e71c6fb$var$triggerChangeHandlers("virtual", e), $507fabe10e71c6fb$var$hasEventBeforeFocus = !1, $507fabe10e71c6fb$var$hasBlurredWindowRecently = !1);
}
function $507fabe10e71c6fb$var$handleWindowBlur() {
  $507fabe10e71c6fb$var$hasEventBeforeFocus = !1, $507fabe10e71c6fb$var$hasBlurredWindowRecently = !0;
}
function $507fabe10e71c6fb$var$setupGlobalFocusEvents(e) {
  if (typeof window > "u" || $507fabe10e71c6fb$export$d90243b58daecda7.get($431fbd86ca7dc216$export$f21a1ffae260145a(e))) return;
  const r = $431fbd86ca7dc216$export$f21a1ffae260145a(e), n = $431fbd86ca7dc216$export$b204af158042fbac(e);
  let a = r.HTMLElement.prototype.focus;
  r.HTMLElement.prototype.focus = function() {
    $507fabe10e71c6fb$var$hasEventBeforeFocus = !0, a.apply(this, arguments);
  }, n.addEventListener("keydown", $507fabe10e71c6fb$var$handleKeyboardEvent, !0), n.addEventListener("keyup", $507fabe10e71c6fb$var$handleKeyboardEvent, !0), n.addEventListener("click", $507fabe10e71c6fb$var$handleClickEvent, !0), r.addEventListener("focus", $507fabe10e71c6fb$var$handleFocusEvent, !0), r.addEventListener("blur", $507fabe10e71c6fb$var$handleWindowBlur, !1), typeof PointerEvent < "u" ? (n.addEventListener("pointerdown", $507fabe10e71c6fb$var$handlePointerEvent, !0), n.addEventListener("pointermove", $507fabe10e71c6fb$var$handlePointerEvent, !0), n.addEventListener("pointerup", $507fabe10e71c6fb$var$handlePointerEvent, !0)) : (n.addEventListener("mousedown", $507fabe10e71c6fb$var$handlePointerEvent, !0), n.addEventListener("mousemove", $507fabe10e71c6fb$var$handlePointerEvent, !0), n.addEventListener("mouseup", $507fabe10e71c6fb$var$handlePointerEvent, !0)), r.addEventListener("beforeunload", () => {
    $507fabe10e71c6fb$var$tearDownWindowFocusTracking(e);
  }, {
    once: !0
  }), $507fabe10e71c6fb$export$d90243b58daecda7.set(r, {
    focus: a
  });
}
const $507fabe10e71c6fb$var$tearDownWindowFocusTracking = (e, r) => {
  const n = $431fbd86ca7dc216$export$f21a1ffae260145a(e), a = $431fbd86ca7dc216$export$b204af158042fbac(e);
  r && a.removeEventListener("DOMContentLoaded", r), $507fabe10e71c6fb$export$d90243b58daecda7.has(n) && (n.HTMLElement.prototype.focus = $507fabe10e71c6fb$export$d90243b58daecda7.get(n).focus, a.removeEventListener("keydown", $507fabe10e71c6fb$var$handleKeyboardEvent, !0), a.removeEventListener("keyup", $507fabe10e71c6fb$var$handleKeyboardEvent, !0), a.removeEventListener("click", $507fabe10e71c6fb$var$handleClickEvent, !0), n.removeEventListener("focus", $507fabe10e71c6fb$var$handleFocusEvent, !0), n.removeEventListener("blur", $507fabe10e71c6fb$var$handleWindowBlur, !1), typeof PointerEvent < "u" ? (a.removeEventListener("pointerdown", $507fabe10e71c6fb$var$handlePointerEvent, !0), a.removeEventListener("pointermove", $507fabe10e71c6fb$var$handlePointerEvent, !0), a.removeEventListener("pointerup", $507fabe10e71c6fb$var$handlePointerEvent, !0)) : (a.removeEventListener("mousedown", $507fabe10e71c6fb$var$handlePointerEvent, !0), a.removeEventListener("mousemove", $507fabe10e71c6fb$var$handlePointerEvent, !0), a.removeEventListener("mouseup", $507fabe10e71c6fb$var$handlePointerEvent, !0)), $507fabe10e71c6fb$export$d90243b58daecda7.delete(n));
};
function $507fabe10e71c6fb$export$2f1888112f558a7d(e) {
  const r = $431fbd86ca7dc216$export$b204af158042fbac(e);
  let n;
  return r.readyState !== "loading" ? $507fabe10e71c6fb$var$setupGlobalFocusEvents(e) : (n = () => {
    $507fabe10e71c6fb$var$setupGlobalFocusEvents(e);
  }, r.addEventListener("DOMContentLoaded", n)), () => $507fabe10e71c6fb$var$tearDownWindowFocusTracking(e, n);
}
typeof document < "u" && $507fabe10e71c6fb$export$2f1888112f558a7d();
function $e8a7022cf87cba2a$export$36da96379f79f245(e) {
  let { onMoveStart: r, onMove: n, onMoveEnd: a } = e, o = useRef({
    didMove: !1,
    lastPosition: null,
    id: null
  }), { addGlobalListener: i, removeGlobalListener: c } = $03deb23ff14920c4$export$4eaf04e54aa8eed6(), l = $8ae05eaa5c114e9c$export$7f54fc3180508a52((u, m, $, v) => {
    $ === 0 && v === 0 || (o.current.didMove || (o.current.didMove = !0, r?.({
      type: "movestart",
      pointerType: m,
      shiftKey: u.shiftKey,
      metaKey: u.metaKey,
      ctrlKey: u.ctrlKey,
      altKey: u.altKey
    })), n?.({
      type: "move",
      pointerType: m,
      deltaX: $,
      deltaY: v,
      shiftKey: u.shiftKey,
      metaKey: u.metaKey,
      ctrlKey: u.ctrlKey,
      altKey: u.altKey
    }));
  }), p = $8ae05eaa5c114e9c$export$7f54fc3180508a52((u, m) => {
    $14c0b72509d70225$export$b0d6fa1ab32e3295(), o.current.didMove && a?.({
      type: "moveend",
      pointerType: m,
      shiftKey: u.shiftKey,
      metaKey: u.metaKey,
      ctrlKey: u.ctrlKey,
      altKey: u.altKey
    });
  });
  return {
    moveProps: useMemo(() => {
      let u = {}, m = () => {
        $14c0b72509d70225$export$16a4697467175487(), o.current.didMove = !1;
      };
      if (typeof PointerEvent > "u") {
        let v = (b) => {
          if (b.button === 0) {
            var _, R, w, g;
            l(b, "mouse", b.pageX - ((w = (_ = o.current.lastPosition) === null || _ === void 0 ? void 0 : _.pageX) !== null && w !== void 0 ? w : 0), b.pageY - ((g = (R = o.current.lastPosition) === null || R === void 0 ? void 0 : R.pageY) !== null && g !== void 0 ? g : 0)), o.current.lastPosition = {
              pageX: b.pageX,
              pageY: b.pageY
            };
          }
        }, y = (b) => {
          b.button === 0 && (p(b, "mouse"), c(window, "mousemove", v, !1), c(window, "mouseup", y, !1));
        };
        u.onMouseDown = (b) => {
          b.button === 0 && (m(), b.stopPropagation(), b.preventDefault(), o.current.lastPosition = {
            pageX: b.pageX,
            pageY: b.pageY
          }, i(window, "mousemove", v, !1), i(window, "mouseup", y, !1));
        };
        let d = (b) => {
          let _ = [
            ...b.changedTouches
          ].findIndex(({ identifier: I }) => I === o.current.id);
          if (_ >= 0) {
            var R, w;
            let { pageX: I, pageY: D } = b.changedTouches[_];
            var g, O;
            l(b, "touch", I - ((g = (R = o.current.lastPosition) === null || R === void 0 ? void 0 : R.pageX) !== null && g !== void 0 ? g : 0), D - ((O = (w = o.current.lastPosition) === null || w === void 0 ? void 0 : w.pageY) !== null && O !== void 0 ? O : 0)), o.current.lastPosition = {
              pageX: I,
              pageY: D
            };
          }
        }, h = (b) => {
          [
            ...b.changedTouches
          ].findIndex(({ identifier: R }) => R === o.current.id) >= 0 && (p(b, "touch"), o.current.id = null, c(window, "touchmove", d), c(window, "touchend", h), c(window, "touchcancel", h));
        };
        u.onTouchStart = (b) => {
          if (b.changedTouches.length === 0 || o.current.id != null) return;
          let { pageX: _, pageY: R, identifier: w } = b.changedTouches[0];
          m(), b.stopPropagation(), b.preventDefault(), o.current.lastPosition = {
            pageX: _,
            pageY: R
          }, o.current.id = w, i(window, "touchmove", d, !1), i(window, "touchend", h, !1), i(window, "touchcancel", h, !1);
        };
      } else {
        let v = (d) => {
          if (d.pointerId === o.current.id) {
            var h, b;
            let w = d.pointerType || "mouse";
            var _, R;
            l(d, w, d.pageX - ((_ = (h = o.current.lastPosition) === null || h === void 0 ? void 0 : h.pageX) !== null && _ !== void 0 ? _ : 0), d.pageY - ((R = (b = o.current.lastPosition) === null || b === void 0 ? void 0 : b.pageY) !== null && R !== void 0 ? R : 0)), o.current.lastPosition = {
              pageX: d.pageX,
              pageY: d.pageY
            };
          }
        }, y = (d) => {
          if (d.pointerId === o.current.id) {
            let h = d.pointerType || "mouse";
            p(d, h), o.current.id = null, c(window, "pointermove", v, !1), c(window, "pointerup", y, !1), c(window, "pointercancel", y, !1);
          }
        };
        u.onPointerDown = (d) => {
          d.button === 0 && o.current.id == null && (m(), d.stopPropagation(), d.preventDefault(), o.current.lastPosition = {
            pageX: d.pageX,
            pageY: d.pageY
          }, o.current.id = d.pointerId, i(window, "pointermove", v, !1), i(window, "pointerup", y, !1), i(window, "pointercancel", y, !1));
        };
      }
      let $ = (v, y, d) => {
        m(), l(v, "keyboard", y, d), p(v, "keyboard");
      };
      return u.onKeyDown = (v) => {
        switch (v.key) {
          case "Left":
          case "ArrowLeft":
            v.preventDefault(), v.stopPropagation(), $(v, -1, 0);
            break;
          case "Right":
          case "ArrowRight":
            v.preventDefault(), v.stopPropagation(), $(v, 1, 0);
            break;
          case "Up":
          case "ArrowUp":
            v.preventDefault(), v.stopPropagation(), $(v, 0, -1);
            break;
          case "Down":
          case "ArrowDown":
            v.preventDefault(), v.stopPropagation(), $(v, 0, 1);
            break;
        }
      }, u;
    }, [
      o,
      i,
      c,
      l,
      p
    ])
  };
}
var __assign = function() {
  return __assign = Object.assign || function(r) {
    for (var n, a = 1, o = arguments.length; a < o; a++) {
      n = arguments[a];
      for (var i in n) Object.prototype.hasOwnProperty.call(n, i) && (r[i] = n[i]);
    }
    return r;
  }, __assign.apply(this, arguments);
};
function __rest(e, r) {
  var n = {};
  for (var a in e) Object.prototype.hasOwnProperty.call(e, a) && r.indexOf(a) < 0 && (n[a] = e[a]);
  if (e != null && typeof Object.getOwnPropertySymbols == "function")
    for (var o = 0, a = Object.getOwnPropertySymbols(e); o < a.length; o++)
      r.indexOf(a[o]) < 0 && Object.prototype.propertyIsEnumerable.call(e, a[o]) && (n[a[o]] = e[a[o]]);
  return n;
}
function __spreadArray(e, r, n) {
  if (n || arguments.length === 2) for (var a = 0, o = r.length, i; a < o; a++)
    (i || !(a in r)) && (i || (i = Array.prototype.slice.call(r, 0, a)), i[a] = r[a]);
  return e.concat(i || Array.prototype.slice.call(r));
}
typeof SuppressedError == "function" && SuppressedError;
var zeroRightClassName = "right-scroll-bar-position", fullWidthClassName = "width-before-scroll-bar", noScrollbarsClassName = "with-scroll-bars-hidden", removedBarSizeVariable = "--removed-body-scroll-bar-size";
function assignRef(e, r) {
  return typeof e == "function" ? e(r) : e && (e.current = r), e;
}
function useCallbackRef(e, r) {
  var n = useState(function() {
    return {
      // value
      value: e,
      // last callback
      callback: r,
      // "memoized" public interface
      facade: {
        get current() {
          return n.value;
        },
        set current(a) {
          var o = n.value;
          o !== a && (n.value = a, n.callback(a, o));
        }
      }
    };
  })[0];
  return n.callback = r, n.facade;
}
var useIsomorphicLayoutEffect = typeof window < "u" ? React.useLayoutEffect : React.useEffect, currentValues = /* @__PURE__ */ new WeakMap();
function useMergeRefs(e, r) {
  var n = useCallbackRef(null, function(a) {
    return e.forEach(function(o) {
      return assignRef(o, a);
    });
  });
  return useIsomorphicLayoutEffect(function() {
    var a = currentValues.get(n);
    if (a) {
      var o = new Set(a), i = new Set(e), c = n.current;
      o.forEach(function(l) {
        i.has(l) || assignRef(l, null);
      }), i.forEach(function(l) {
        o.has(l) || assignRef(l, c);
      });
    }
    currentValues.set(n, e);
  }, [e]), n;
}
function ItoI(e) {
  return e;
}
function innerCreateMedium(e, r) {
  r === void 0 && (r = ItoI);
  var n = [], a = !1, o = {
    read: function() {
      if (a)
        throw new Error("Sidecar: could not `read` from an `assigned` medium. `read` could be used only with `useMedium`.");
      return n.length ? n[n.length - 1] : e;
    },
    useMedium: function(i) {
      var c = r(i, a);
      return n.push(c), function() {
        n = n.filter(function(l) {
          return l !== c;
        });
      };
    },
    assignSyncMedium: function(i) {
      for (a = !0; n.length; ) {
        var c = n;
        n = [], c.forEach(i);
      }
      n = {
        push: function(l) {
          return i(l);
        },
        filter: function() {
          return n;
        }
      };
    },
    assignMedium: function(i) {
      a = !0;
      var c = [];
      if (n.length) {
        var l = n;
        n = [], l.forEach(i), c = n;
      }
      var p = function() {
        var u = c;
        c = [], u.forEach(i);
      }, f = function() {
        return Promise.resolve().then(p);
      };
      f(), n = {
        push: function(u) {
          c.push(u), f();
        },
        filter: function(u) {
          return c = c.filter(u), n;
        }
      };
    }
  };
  return o;
}
function createSidecarMedium(e) {
  e === void 0 && (e = {});
  var r = innerCreateMedium(null);
  return r.options = __assign({ async: !0, ssr: !1 }, e), r;
}
var SideCar$1 = function(e) {
  var r = e.sideCar, n = __rest(e, ["sideCar"]);
  if (!r)
    throw new Error("Sidecar: please provide `sideCar` property to import the right car");
  var a = r.read();
  if (!a)
    throw new Error("Sidecar medium not found");
  return React.createElement(a, __assign({}, n));
};
SideCar$1.isSideCarExport = !0;
function exportSidecar(e, r) {
  return e.useMedium(r), SideCar$1;
}
var effectCar = createSidecarMedium(), nothing = function() {
}, RemoveScroll = React.forwardRef(function(e, r) {
  var n = React.useRef(null), a = React.useState({
    onScrollCapture: nothing,
    onWheelCapture: nothing,
    onTouchMoveCapture: nothing
  }), o = a[0], i = a[1], c = e.forwardProps, l = e.children, p = e.className, f = e.removeScrollBar, u = e.enabled, m = e.shards, $ = e.sideCar, v = e.noIsolation, y = e.inert, d = e.allowPinchZoom, h = e.as, b = h === void 0 ? "div" : h, _ = e.gapMode, R = __rest(e, ["forwardProps", "children", "className", "removeScrollBar", "enabled", "shards", "sideCar", "noIsolation", "inert", "allowPinchZoom", "as", "gapMode"]), w = $, g = useMergeRefs([n, r]), O = __assign(__assign({}, R), o);
  return React.createElement(
    React.Fragment,
    null,
    u && React.createElement(w, { sideCar: effectCar, removeScrollBar: f, shards: m, noIsolation: v, inert: y, setCallbacks: i, allowPinchZoom: !!d, lockRef: n, gapMode: _ }),
    c ? React.cloneElement(React.Children.only(l), __assign(__assign({}, O), { ref: g })) : React.createElement(b, __assign({}, O, { className: p, ref: g }), l)
  );
});
RemoveScroll.defaultProps = {
  enabled: !0,
  removeScrollBar: !0,
  inert: !1
};
RemoveScroll.classNames = {
  fullWidth: fullWidthClassName,
  zeroRight: zeroRightClassName
};
var getNonce = function() {
  if (typeof __webpack_nonce__ < "u")
    return __webpack_nonce__;
};
function makeStyleTag() {
  if (!document)
    return null;
  var e = document.createElement("style");
  e.type = "text/css";
  var r = getNonce();
  return r && e.setAttribute("nonce", r), e;
}
function injectStyles(e, r) {
  e.styleSheet ? e.styleSheet.cssText = r : e.appendChild(document.createTextNode(r));
}
function insertStyleTag(e) {
  var r = document.head || document.getElementsByTagName("head")[0];
  r.appendChild(e);
}
var stylesheetSingleton = function() {
  var e = 0, r = null;
  return {
    add: function(n) {
      e == 0 && (r = makeStyleTag()) && (injectStyles(r, n), insertStyleTag(r)), e++;
    },
    remove: function() {
      e--, !e && r && (r.parentNode && r.parentNode.removeChild(r), r = null);
    }
  };
}, styleHookSingleton = function() {
  var e = stylesheetSingleton();
  return function(r, n) {
    React.useEffect(function() {
      return e.add(r), function() {
        e.remove();
      };
    }, [r && n]);
  };
}, styleSingleton = function() {
  var e = styleHookSingleton(), r = function(n) {
    var a = n.styles, o = n.dynamic;
    return e(a, o), null;
  };
  return r;
}, zeroGap = {
  left: 0,
  top: 0,
  right: 0,
  gap: 0
}, parse = function(e) {
  return parseInt(e || "", 10) || 0;
}, getOffset = function(e) {
  var r = window.getComputedStyle(document.body), n = r[e === "padding" ? "paddingLeft" : "marginLeft"], a = r[e === "padding" ? "paddingTop" : "marginTop"], o = r[e === "padding" ? "paddingRight" : "marginRight"];
  return [parse(n), parse(a), parse(o)];
}, getGapWidth = function(e) {
  if (e === void 0 && (e = "margin"), typeof window > "u")
    return zeroGap;
  var r = getOffset(e), n = document.documentElement.clientWidth, a = window.innerWidth;
  return {
    left: r[0],
    top: r[1],
    right: r[2],
    gap: Math.max(0, a - n + r[2] - r[0])
  };
}, Style = styleSingleton(), lockAttribute = "data-scroll-locked", getStyles = function(e, r, n, a) {
  var o = e.left, i = e.top, c = e.right, l = e.gap;
  return n === void 0 && (n = "margin"), `
  .`.concat(noScrollbarsClassName, ` {
   overflow: hidden `).concat(a, `;
   padding-right: `).concat(l, "px ").concat(a, `;
  }
  body[`).concat(lockAttribute, `] {
    overflow: hidden `).concat(a, `;
    overscroll-behavior: contain;
    `).concat([
    r && "position: relative ".concat(a, ";"),
    n === "margin" && `
    padding-left: `.concat(o, `px;
    padding-top: `).concat(i, `px;
    padding-right: `).concat(c, `px;
    margin-left:0;
    margin-top:0;
    margin-right: `).concat(l, "px ").concat(a, `;
    `),
    n === "padding" && "padding-right: ".concat(l, "px ").concat(a, ";")
  ].filter(Boolean).join(""), `
  }
  
  .`).concat(zeroRightClassName, ` {
    right: `).concat(l, "px ").concat(a, `;
  }
  
  .`).concat(fullWidthClassName, ` {
    margin-right: `).concat(l, "px ").concat(a, `;
  }
  
  .`).concat(zeroRightClassName, " .").concat(zeroRightClassName, ` {
    right: 0 `).concat(a, `;
  }
  
  .`).concat(fullWidthClassName, " .").concat(fullWidthClassName, ` {
    margin-right: 0 `).concat(a, `;
  }
  
  body[`).concat(lockAttribute, `] {
    `).concat(removedBarSizeVariable, ": ").concat(l, `px;
  }
`);
}, getCurrentUseCounter = function() {
  var e = parseInt(document.body.getAttribute(lockAttribute) || "0", 10);
  return isFinite(e) ? e : 0;
}, useLockAttribute = function() {
  React.useEffect(function() {
    return document.body.setAttribute(lockAttribute, (getCurrentUseCounter() + 1).toString()), function() {
      var e = getCurrentUseCounter() - 1;
      e <= 0 ? document.body.removeAttribute(lockAttribute) : document.body.setAttribute(lockAttribute, e.toString());
    };
  }, []);
}, RemoveScrollBar = function(e) {
  var r = e.noRelative, n = e.noImportant, a = e.gapMode, o = a === void 0 ? "margin" : a;
  useLockAttribute();
  var i = React.useMemo(function() {
    return getGapWidth(o);
  }, [o]);
  return React.createElement(Style, { styles: getStyles(i, !r, o, n ? "" : "!important") });
}, passiveSupported = !1;
if (typeof window < "u")
  try {
    var options = Object.defineProperty({}, "passive", {
      get: function() {
        return passiveSupported = !0, !0;
      }
    });
    window.addEventListener("test", options, options), window.removeEventListener("test", options, options);
  } catch {
    passiveSupported = !1;
  }
var nonPassive = passiveSupported ? { passive: !1 } : !1, alwaysContainsScroll = function(e) {
  return e.tagName === "TEXTAREA";
}, elementCanBeScrolled = function(e, r) {
  if (!(e instanceof Element))
    return !1;
  var n = window.getComputedStyle(e);
  return (
    // not-not-scrollable
    n[r] !== "hidden" && // contains scroll inside self
    !(n.overflowY === n.overflowX && !alwaysContainsScroll(e) && n[r] === "visible")
  );
}, elementCouldBeVScrolled = function(e) {
  return elementCanBeScrolled(e, "overflowY");
}, elementCouldBeHScrolled = function(e) {
  return elementCanBeScrolled(e, "overflowX");
}, locationCouldBeScrolled = function(e, r) {
  var n = r.ownerDocument, a = r;
  do {
    typeof ShadowRoot < "u" && a instanceof ShadowRoot && (a = a.host);
    var o = elementCouldBeScrolled(e, a);
    if (o) {
      var i = getScrollVariables(e, a), c = i[1], l = i[2];
      if (c > l)
        return !0;
    }
    a = a.parentNode;
  } while (a && a !== n.body);
  return !1;
}, getVScrollVariables = function(e) {
  var r = e.scrollTop, n = e.scrollHeight, a = e.clientHeight;
  return [
    r,
    n,
    a
  ];
}, getHScrollVariables = function(e) {
  var r = e.scrollLeft, n = e.scrollWidth, a = e.clientWidth;
  return [
    r,
    n,
    a
  ];
}, elementCouldBeScrolled = function(e, r) {
  return e === "v" ? elementCouldBeVScrolled(r) : elementCouldBeHScrolled(r);
}, getScrollVariables = function(e, r) {
  return e === "v" ? getVScrollVariables(r) : getHScrollVariables(r);
}, getDirectionFactor = function(e, r) {
  return e === "h" && r === "rtl" ? -1 : 1;
}, handleScroll = function(e, r, n, a, o) {
  var i = getDirectionFactor(e, window.getComputedStyle(r).direction), c = i * a, l = n.target, p = r.contains(l), f = !1, u = c > 0, m = 0, $ = 0;
  do {
    var v = getScrollVariables(e, l), y = v[0], d = v[1], h = v[2], b = d - h - i * y;
    (y || b) && elementCouldBeScrolled(e, l) && (m += b, $ += y), l instanceof ShadowRoot ? l = l.host : l = l.parentNode;
  } while (
    // portaled content
    !p && l !== document.body || // self content
    p && (r.contains(l) || r === l)
  );
  return (u && Math.abs(m) < 1 || !u && Math.abs($) < 1) && (f = !0), f;
}, getTouchXY = function(e) {
  return "changedTouches" in e ? [e.changedTouches[0].clientX, e.changedTouches[0].clientY] : [0, 0];
}, getDeltaXY = function(e) {
  return [e.deltaX, e.deltaY];
}, extractRef = function(e) {
  return e && "current" in e ? e.current : e;
}, deltaCompare = function(e, r) {
  return e[0] === r[0] && e[1] === r[1];
}, generateStyle = function(e) {
  return `
  .block-interactivity-`.concat(e, ` {pointer-events: none;}
  .allow-interactivity-`).concat(e, ` {pointer-events: all;}
`);
}, idCounter = 0, lockStack = [];
function RemoveScrollSideCar(e) {
  var r = React.useRef([]), n = React.useRef([0, 0]), a = React.useRef(), o = React.useState(idCounter++)[0], i = React.useState(styleSingleton)[0], c = React.useRef(e);
  React.useEffect(function() {
    c.current = e;
  }, [e]), React.useEffect(function() {
    if (e.inert) {
      document.body.classList.add("block-interactivity-".concat(o));
      var d = __spreadArray([e.lockRef.current], (e.shards || []).map(extractRef), !0).filter(Boolean);
      return d.forEach(function(h) {
        return h.classList.add("allow-interactivity-".concat(o));
      }), function() {
        document.body.classList.remove("block-interactivity-".concat(o)), d.forEach(function(h) {
          return h.classList.remove("allow-interactivity-".concat(o));
        });
      };
    }
  }, [e.inert, e.lockRef.current, e.shards]);
  var l = React.useCallback(function(d, h) {
    if ("touches" in d && d.touches.length === 2 || d.type === "wheel" && d.ctrlKey)
      return !c.current.allowPinchZoom;
    var b = getTouchXY(d), _ = n.current, R = "deltaX" in d ? d.deltaX : _[0] - b[0], w = "deltaY" in d ? d.deltaY : _[1] - b[1], g, O = d.target, I = Math.abs(R) > Math.abs(w) ? "h" : "v";
    if ("touches" in d && I === "h" && O.type === "range")
      return !1;
    var D = locationCouldBeScrolled(I, O);
    if (!D)
      return !0;
    if (D ? g = I : (g = I === "v" ? "h" : "v", D = locationCouldBeScrolled(I, O)), !D)
      return !1;
    if (!a.current && "changedTouches" in d && (R || w) && (a.current = g), !g)
      return !0;
    var L = a.current || g;
    return handleScroll(L, h, d, L === "h" ? R : w);
  }, []), p = React.useCallback(function(d) {
    var h = d;
    if (!(!lockStack.length || lockStack[lockStack.length - 1] !== i)) {
      var b = "deltaY" in h ? getDeltaXY(h) : getTouchXY(h), _ = r.current.filter(function(g) {
        return g.name === h.type && (g.target === h.target || h.target === g.shadowParent) && deltaCompare(g.delta, b);
      })[0];
      if (_ && _.should) {
        h.cancelable && h.preventDefault();
        return;
      }
      if (!_) {
        var R = (c.current.shards || []).map(extractRef).filter(Boolean).filter(function(g) {
          return g.contains(h.target);
        }), w = R.length > 0 ? l(h, R[0]) : !c.current.noIsolation;
        w && h.cancelable && h.preventDefault();
      }
    }
  }, []), f = React.useCallback(function(d, h, b, _) {
    var R = { name: d, delta: h, target: b, should: _, shadowParent: getOutermostShadowParent(b) };
    r.current.push(R), setTimeout(function() {
      r.current = r.current.filter(function(w) {
        return w !== R;
      });
    }, 1);
  }, []), u = React.useCallback(function(d) {
    n.current = getTouchXY(d), a.current = void 0;
  }, []), m = React.useCallback(function(d) {
    f(d.type, getDeltaXY(d), d.target, l(d, e.lockRef.current));
  }, []), $ = React.useCallback(function(d) {
    f(d.type, getTouchXY(d), d.target, l(d, e.lockRef.current));
  }, []);
  React.useEffect(function() {
    return lockStack.push(i), e.setCallbacks({
      onScrollCapture: m,
      onWheelCapture: m,
      onTouchMoveCapture: $
    }), document.addEventListener("wheel", p, nonPassive), document.addEventListener("touchmove", p, nonPassive), document.addEventListener("touchstart", u, nonPassive), function() {
      lockStack = lockStack.filter(function(d) {
        return d !== i;
      }), document.removeEventListener("wheel", p, nonPassive), document.removeEventListener("touchmove", p, nonPassive), document.removeEventListener("touchstart", u, nonPassive);
    };
  }, []);
  var v = e.removeScrollBar, y = e.inert;
  return React.createElement(
    React.Fragment,
    null,
    y ? React.createElement(i, { styles: generateStyle(o) }) : null,
    v ? React.createElement(RemoveScrollBar, { gapMode: e.gapMode }) : null
  );
}
function getOutermostShadowParent(e) {
  for (var r = null; e !== null; )
    e instanceof ShadowRoot && (r = e.host, e = e.host), e = e.parentNode;
  return r;
}
const SideCar = exportSidecar(effectCar, RemoveScrollSideCar);
var ReactRemoveScroll = React.forwardRef(function(e, r) {
  return React.createElement(RemoveScroll, __assign({}, e, { ref: r, sideCar: SideCar }));
});
ReactRemoveScroll.classNames = RemoveScroll.classNames;
const aurora = {
  durations: {
    slow: "400ms"
  },
  easings: {
    easeInOut: "cubic-bezier(0.645, 0.045, 0.355, 1)"
  }
}, {
  durations,
  easings
} = aurora;
function _typeof(e) {
  return _typeof = typeof Symbol == "function" && typeof Symbol.iterator == "symbol" ? function(r) {
    return typeof r;
  } : function(r) {
    return r && typeof Symbol == "function" && r.constructor === Symbol && r !== Symbol.prototype ? "symbol" : typeof r;
  }, _typeof(e);
}
function toPropertyKey(e) {
  var r = function(a, o) {
    if (_typeof(a) != "object" || !a) return a;
    var i = a[Symbol.toPrimitive];
    if (i !== void 0) {
      var c = i.call(a, o);
      if (_typeof(c) != "object") return c;
      throw new TypeError("@@toPrimitive must return a primitive value.");
    }
    return (o === "string" ? String : Number)(a);
  }(e, "string");
  return _typeof(r) == "symbol" ? r : r + "";
}
function _defineProperty(e, r, n) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: n, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = n, e;
}
function _objectWithoutProperties(e, r) {
  if (e == null) return {};
  var n, a, o = function(l, p) {
    if (l == null) return {};
    var f = {};
    for (var u in l) if ({}.hasOwnProperty.call(l, u)) {
      if (p.indexOf(u) !== -1) continue;
      f[u] = l[u];
    }
    return f;
  }(e, r);
  if (Object.getOwnPropertySymbols) {
    var i = Object.getOwnPropertySymbols(e);
    for (a = 0; a < i.length; a++) n = i[a], r.indexOf(n) === -1 && {}.propertyIsEnumerable.call(e, n) && (o[n] = e[n]);
  }
  return o;
}
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function(e) {
    for (var r = 1; r < arguments.length; r++) {
      var n = arguments[r];
      for (var a in n) ({}).hasOwnProperty.call(n, a) && (e[a] = n[a]);
    }
    return e;
  }, _extends.apply(null, arguments);
}
const x = ["direction", "ariaLabel", "positionControl", "sx", "onChevronClick", "inverseControls", "variant"];
function ownKeys$4(e, r) {
  var n = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var a = Object.getOwnPropertySymbols(e);
    r && (a = a.filter(function(o) {
      return Object.getOwnPropertyDescriptor(e, o).enumerable;
    })), n.push.apply(n, a);
  }
  return n;
}
function _objectSpread$4(e) {
  for (var r = 1; r < arguments.length; r++) {
    var n = arguments[r] != null ? arguments[r] : {};
    r % 2 ? ownKeys$4(Object(n), !0).forEach(function(a) {
      _defineProperty(e, a, n[a]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(n)) : ownKeys$4(Object(n)).forEach(function(a) {
      Object.defineProperty(e, a, Object.getOwnPropertyDescriptor(n, a));
    });
  }
  return e;
}
const CarouselControllers = (e) => {
  let { direction: r = "left", ariaLabel: n, positionControl: a = "outside", sx: o, onChevronClick: i, inverseControls: c = !1, variant: l } = e, p = _objectWithoutProperties(e, x);
  const f = { left: React__default.createElement(ArrowLeft, { "aria-hidden": !0 }), right: React__default.createElement(ArrowRight, { "aria-hidden": !0 }) }[r];
  return React__default.createElement(Box, { sx: _objectSpread$4(_objectSpread$4({ zIndex: 1, flexShrink: 0 }, (() => {
    switch (a) {
      case "outside":
        return { position: "relative", paddingInlineEnd: r === "left" ? 2 : "", paddingInlineStart: r === "right" ? 2 : "" };
      case "inside":
        const u = l === "centered" || l === "peek" ? 2 : 1;
        return { position: "absolute", right: r === "right" ? u : "", left: r === "left" ? u : "" };
      case "hover":
        return { position: "relative", transform: r === "right" ? "translate(-0.5rem)" : "translate(0.5rem)" };
    }
  })()), o) }, React__default.createElement(Button, _extends({ iconOnly: !0, variant: c ? "inverse" : "secondary", size: "sm", sx: { borderRadius: "full", borderColor: "transparent", boxShadow: "low", backgroundColor: c ? "background.action.inverse" : "background.action.weaker", color: c ? "foreground.inverse" : "foreground.strong", '&:hover:not([aria-disabled="true"])': { backgroundColor: c ? "background.action.inverse.hover" : "background.action.hover", borderColor: "transparent" }, '&:active:not([aria-disabled="true"])': { backgroundColor: c ? "background.action.inverse.active" : "background.action.weaker.active" }, "&:focus": { borderColor: "transparent", borderImageWidth: 0, "&:not([data-focus-visible-added]):not(:active)": { boxShadow: "low" }, "&::before": { display: "block" } } }, onClick: (u) => {
    i(u, r);
  }, "data-testid": `ipl-carousel-nav-${r}`, type: "button", title: n }, p), f));
};
CarouselControllers.displayName = "CarouselControllers";
const C = ["active", "ariaLabel", "isHidden", "size", "sx"];
function ownKeys$3(e, r) {
  var n = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var a = Object.getOwnPropertySymbols(e);
    r && (a = a.filter(function(o) {
      return Object.getOwnPropertyDescriptor(e, o).enumerable;
    })), n.push.apply(n, a);
  }
  return n;
}
function _objectSpread$3(e) {
  for (var r = 1; r < arguments.length; r++) {
    var n = arguments[r] != null ? arguments[r] : {};
    r % 2 ? ownKeys$3(Object(n), !0).forEach(function(a) {
      _defineProperty(e, a, n[a]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(n)) : ownKeys$3(Object(n)).forEach(function(a) {
      Object.defineProperty(e, a, Object.getOwnPropertyDescriptor(n, a));
    });
  }
  return e;
}
const CarouselDot = (e) => {
  let { active: r, ariaLabel: n, isHidden: a, size: o, sx: i } = e, c = _objectWithoutProperties(e, C);
  const l = useCallback(() => {
    switch (o) {
      case "sm":
        return "3";
      case "md":
        return "4";
      default:
        return "5";
    }
  }, [o]);
  return React__default.createElement(Box, { sx: _objectSpread$3({ visibility: a ? "hidden" : "visible" }, i), hidden: a, as: "li" }, React__default.createElement(Button, _extends({ iconOnly: !0, "aria-current": r, "aria-label": n, size: "sm", sx: { borderRadius: "full", paddingBlockStart: 0, paddingBlockEnd: 0, paddingInlineStart: 0, paddingInlineEnd: 0, borderWidth: 1, borderColor: "transparent", borderStyle: "solid", "&:focus::before": { display: "block" } }, type: "button", variant: "tertiary" }, c), React__default.createElement(Icon$1, { sx: { color: r ? "foreground.weak" : "transparent", stroke: r ? "none" : "border" }, "aria-hidden": !0 }, React__default.createElement("circle", { cx: "12", cy: "12", r: l() }))));
};
CarouselDot.displayName = "CarouselDot";
const P = ["activeIndex", "dotAriaLabel", "dotItems", "carouselAriaLabel", "onDotClicked", "sx", "variant"];
function ownKeys$2(e, r) {
  var n = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var a = Object.getOwnPropertySymbols(e);
    r && (a = a.filter(function(o) {
      return Object.getOwnPropertyDescriptor(e, o).enumerable;
    })), n.push.apply(n, a);
  }
  return n;
}
function _objectSpread$2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var n = arguments[r] != null ? arguments[r] : {};
    r % 2 ? ownKeys$2(Object(n), !0).forEach(function(a) {
      _defineProperty(e, a, n[a]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(n)) : ownKeys$2(Object(n)).forEach(function(a) {
      Object.defineProperty(e, a, Object.getOwnPropertyDescriptor(n, a));
    });
  }
  return e;
}
const CarouselDots = (e) => {
  let { activeIndex: r, dotAriaLabel: n = (m) => `View slide ${m}`, dotItems: a, carouselAriaLabel: o = "carousel", onDotClicked: i, sx: c, variant: l } = e, p = _objectWithoutProperties(e, P);
  const f = (m) => {
    const $ = Math.abs(m - r);
    return $ === 0 ? "lg" : $ === 1 ? "md" : "sm";
  }, u = useMemo(() => a.map((m, $) => $).filter((m) => l !== "3-up" || m % 3 == 0), [a, l]);
  return React__default.createElement(Box, { as: "nav", "aria-label": o, sx: { inlineSize: "100%" } }, React__default.createElement(Box, _extends({}, p, { sx: _objectSpread$2({ display: "flex", justifyContent: "center", listStyle: "none", paddingInlineStart: 0, paddingInlineEnd: 0, paddingBlockStart: 0, paddingBlockEnd: 0 }, c), as: "ol" }), u.map((m) => React__default.createElement(CarouselDot, { active: m === r, ariaLabel: n(m + 1, a.length), key: `key-${m}`, onClick: ($) => i($, m), size: f(m) }))));
};
function ownKeys$1(e, r) {
  var n = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var a = Object.getOwnPropertySymbols(e);
    r && (a = a.filter(function(o) {
      return Object.getOwnPropertyDescriptor(e, o).enumerable;
    })), n.push.apply(n, a);
  }
  return n;
}
function _objectSpread$1(e) {
  for (var r = 1; r < arguments.length; r++) {
    var n = arguments[r] != null ? arguments[r] : {};
    r % 2 ? ownKeys$1(Object(n), !0).forEach(function(a) {
      _defineProperty(e, a, n[a]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(n)) : ownKeys$1(Object(n)).forEach(function(a) {
      Object.defineProperty(e, a, Object.getOwnPropertyDescriptor(n, a));
    });
  }
  return e;
}
CarouselDots.displayName = "CarouselDots";
const CarouselIndicatorBar = (e) => {
  let { activeIndex: r, carouselAriaLabel: n, dotLabel: a, items: o, positionControl: i, positionCharacterCounter: c, showDots: l, showCharacterCounter: p, onDotSelected: f, variant: u, characterCounterValues: m } = e;
  return React__default.createElement(React__default.Fragment, null, (l || p) && React__default.createElement(Box, { sx: { display: "flex", justifyContent: "center", position: "relative", marginBlockStart: 2, marginBlockEnd: 2 } }, l && React__default.createElement(CarouselDots, { activeIndex: r, carouselAriaLabel: n, dotAriaLabel: a, dotItems: o, onDotClicked: ($, v) => {
    f(v);
  }, variant: u }), o.length > 1 && p && c === "outside" && React__default.createElement(Box, { sx: _objectSpread$1({ alignItems: "center", blockSize: "100%", display: "flex", position: "absolute" }, { outside: { right: "2.75rem" }, inside: { right: 0 }, hover: { right: "2.25rem" } }[i]) }, React__default.createElement(CharacterCounter, m))), o.length > 1 && p && c === "inside" && React__default.createElement(CharacterCounter, _extends({}, m, { sx: _objectSpread$1({ position: "absolute", insetBlockStart: 4, paddingBlock: 1, paddingInline: 2, borderRadius: "sm", backgroundColor: "background.inverse", opacity: 0.75, color: "foreground.inverse" }, { outside: { right: "3.75rem" }, inside: { right: 4 }, hover: { right: "3.25rem" } }[i]) })));
};
CarouselIndicatorBar.displayName = "CarouselIndicatorBar";
const S = 1.25, CarouselSlider = (e) => {
  let { activeIndex: r, setActiveIndex: n, items: a, ofLabel: o = (w, g) => `${w} of ${g}`, variant: i, slideRoleTranslation: c = "slide" } = e;
  const l = useTheme().direction === "rtl", p = (() => {
    let w = 1;
    return i === "centered" || i === "3-up" ? w = 3 : i === "peek" && (w = S), 100 / w * a.length;
  })(), f = 100 / a.length, u = `ipl-carousel-${useId()}`, m = useRef(null), $ = useRef(null), v = { x: 0, y: 0, pointerType: "" }, [y, d] = useState(v), { moveProps: h } = $e8a7022cf87cba2a$export$36da96379f79f245({ onMoveStart: () => {
    d(v), $.current = setTimeout(() => {
      $.current = null;
    }, 300);
  }, onMove: (w) => {
    let { deltaX: g, deltaY: O, pointerType: I } = w;
    d((D) => ({ x: D.x + g, y: D.y + O, pointerType: I }));
  }, onMoveEnd: () => {
    const w = $.current && Math.abs(y.x) > 50 && Math.abs(y.y) < 200 ? y.x < 0 ? "left" : "right" : null;
    let g = null, O = null;
    if (m.current) {
      const j = m.current.clientWidth;
      i === "centered" || i === "full" ? (g = j / 2, O = g) : i === "peek" ? (O = j * S / 2, g = O - (j * S - j)) : i === "3-up" && (g = 3 * j / 2, O = g);
    }
    const I = i === "3-up" ? 3 : 1, D = () => {
      const j = r + I, M = i === "3-up" ? a.length - r <= 3 : j >= a.length;
      n(M ? 0 : j);
    }, L = () => {
      const j = r - I, M = i === "3-up" ? a.length - a.length % 3 : a.length - 1;
      n(j >= 0 ? j : M);
    };
    g && (w === "left" || y.x < -g) && (l ? L() : D()), O && (w === "right" || y.x > O) && (l ? D() : L()), d(v);
  } }), b = Math.abs(y.x) > 10, _ = b ? y.x : 0, R = b && y.pointerType === "touch";
  return React__default.createElement(ReactRemoveScroll, { enabled: R }, React__default.createElement(Box, _extends({ sx: { display: "flex", flexDirection: "row", listStyle: "none", transform: i === "centered" ? `translateX(calc(${(f - f * r) * (l ? -1 : 1)}% + ${_}px))` : `translateX(calc(${(l ? f : -f) * r}% + ${_}px))`, transition: _ !== 0 ? void 0 : `transform ${durations.slow} ${easings.easeInOut}`, inlineSize: `${p}%`, "@media (prefers-reduced-motion: reduce)": { transition: "none" }, touchAction: "pan-y" }, "data-testid": "ipl-carousel-items-container" }, h), a && a.map((w, g) => {
    const O = i === "3-up" ? g - r > -1 && g - r < 3 : r === g, I = { marginInlineStart: i === "full" ? "" : 1, marginInlineEnd: i === "full" ? "" : 1, opacity: O ? 1 : "0.3", pointerEvents: O ? void 0 : "none", transition: `opacity ${durations.slow} ${easings.easeInOut}`, inlineSize: "100%", "@media (prefers-reduced-motion: reduce)": { transition: "none" } }, D = `${u}-slide-${g + 1}-title`;
    return React__default.createElement(Box, { key: `${a.length}-${g}`, role: "group", "aria-disabled": O ? void 0 : "true", "aria-hidden": O ? void 0 : "true", "aria-labelledby": D, "aria-roledescription": c, sx: I, "data-testid": `ipl-carousel-slide-${g + 1}` }, React__default.createElement(VisuallyHidden, { id: D }, c, (L = g + 1, j = a.length, o(L, j))), React__default.createElement(Box, { ref: g === 0 ? m : void 0, sx: { userSelect: "none", blockSize: "100%" } }, w));
    var L, j;
  })));
};
CarouselSlider.displayName = "CarouselSlider";
const k = ["ariaLabels", "variant", "items", "onIndexChange", "positionCharacterCounter", "positionControl", "showCharacterCounter", "showDots", "index", "defaultIndex", "inverseControls", "skipToId"];
function ownKeys(e, r) {
  var n = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var a = Object.getOwnPropertySymbols(e);
    r && (a = a.filter(function(o) {
      return Object.getOwnPropertyDescriptor(e, o).enumerable;
    })), n.push.apply(n, a);
  }
  return n;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var n = arguments[r] != null ? arguments[r] : {};
    r % 2 ? ownKeys(Object(n), !0).forEach(function(a) {
      _defineProperty(e, a, n[a]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(n)) : ownKeys(Object(n)).forEach(function(a) {
      Object.defineProperty(e, a, Object.getOwnPropertyDescriptor(n, a));
    });
  }
  return e;
}
const E = React__default.forwardRef((e, r) => {
  var n;
  let { ariaLabels: a = { carouselDescription: "A carousel", carouselRoleTranslation: "carousel", slideRoleTranslation: "slide", threeUpPage: "page", chevronLeft: "Previous slide", chevronRight: "Next slide", dot: (M) => `View slide ${M}`, of: (M, F) => `${M} of ${F}`, skipLink: "Skip carousel" }, variant: o = "full", items: i, onIndexChange: c, positionCharacterCounter: l = "outside", positionControl: p = "outside", showCharacterCounter: f = !0, showDots: u = !0, index: m, defaultIndex: $ = 0, inverseControls: v = !1, skipToId: y } = e, d = _objectWithoutProperties(e, k);
  const h = useRef(null), b = useRef(null), [_, R] = useState($), w = useResponsive(o);
  let g = _;
  m !== void 0 && m > -1 && (g = m);
  const O = useCallback((M) => {
    c?.(M), h.current && h.current.getBoundingClientRect().y < 0 && h.current.scrollIntoView(), R(M);
  }, [c, R]), I = useCallback(() => {
    if (g === i.length - 1 || w === "3-up" && i.length - g <= 3) return void O(0);
    O(w === "3-up" ? g + 3 : g + 1);
  }, [g, i.length, O, w]), D = useCallback(() => {
    if (g === 0) {
      const M = w === "3-up" ? Math.max(3 * Math.floor((i.length - 1) / 3), 0) : i.length - 1;
      return void O(M);
    }
    O(w === "3-up" ? g - 3 : g - 1);
  }, [O, g, i.length, w]);
  useEffect(() => {
    w === "3-up" && g % 3 && O(3 * Math.floor(g / 3));
  }, [g, w, O]), useEffect(() => {
    m !== void 0 && (m < 0 && O(i.length - 1), m >= i.length && O(0));
  }, [m, i, O]);
  const L = useMemo(() => w === "3-up" ? { limit: Math.ceil(i.length / 3), value: Math.floor(g / 3) + 1 } : { limit: i.length, value: g + 1 }, [i.length, g, w]), j = useTheme().direction === "rtl";
  return React__default.createElement(Box, { as: "section", "aria-label": a.carouselDescription, "aria-roledescription": a.carouselRoleTranslation, ref: r, sx: [{ position: "relative" }, d.sx] }, y && React__default.createElement(KeyboardFocusOnly, { focusElementRef: b }, React__default.createElement(ButtonLink, { href: `#${y}`, ref: b, variant: "secondary", sx: { position: "absolute", insetBlockStart: b.current ? -((n = b.current) === null || n === void 0 ? void 0 : n.clientHeight) / 2 : 0, insetInlineStart: 0, zIndex: 1 } }, a.skipLink)), React__default.createElement(VisuallyHidden, null, React__default.createElement(Text, { "aria-live": "polite", "aria-atomic": "true", "data-testid": "boop" }, w === "3-up" ? a.threeUpPage : a.slideRoleTranslation, " ", a.of && a.of(L.value, L.limit))), React__default.createElement(Box, { sx: { display: "flex", justifyContent: "center", alignItems: "stretch", position: "relative" } }, React__default.createElement(CarouselControllers, { ariaLabel: a.chevronLeft, direction: "left", onChevronClick: D, positionControl: p, inverseControls: v, variant: w, sx: { svg: { transform: j ? "scaleX(-1)" : void 0 }, alignSelf: "center" } }), React__default.createElement(Box, { ref: h, sx: _objectSpread({ inlineSize: "100%", overflow: "hidden" }, { peek: { marginInlineStart: "-0.25rem" }, centered: { marginInlineStart: "-0.25rem", marginInlineEnd: "-0.25rem" }, full: {}, "3-up": {} }[w]) }, React__default.createElement(CarouselSlider, { activeIndex: g, setActiveIndex: O, items: i, ofLabel: a.of, slideRoleTranslation: a.slideRoleTranslation, variant: w })), React__default.createElement(CarouselControllers, { ariaLabel: a.chevronRight, direction: "right", onChevronClick: I, positionControl: p, inverseControls: v, variant: w, sx: { svg: { transform: j ? "scaleX(-1)" : void 0 }, alignSelf: "center" } })), React__default.createElement(CarouselIndicatorBar, { activeIndex: g, carouselAriaLabel: a.carouselDescription, dotLabel: a.dot, items: i, positionControl: p, positionCharacterCounter: l, onDotSelected: O, showCharacterCounter: f, showDots: u, variant: w, characterCounterValues: L }));
});
E.displayName = "Carousel";
const PDFJS_VERSION = "5.2.133", CDN_PATH_NAME = "https://cdn.jsdelivr.net/npm/pdfjs-dist", MAX_PAGES = 5, MAX_WIDTH = 768, IS_PAGE_INCLUDED = () => !0, PAGE_VARIANT = "page", HIGHLIGHT_COLOR = "rgba(0, 128, 0, 0.25)", COMMON_DOCUMENT_WIDTH = 624, loadPDFJSLib = ({
  cdnPathName = CDN_PATH_NAME,
  version = PDFJS_VERSION,
  libUrl: libUrlFromArgs,
  workerUrl: workerUrlFromArgs,
  ...documentInitParameters
} = {}) => new Promise((resolve, reject) => {
  try {
    if (globalThis.pdfjsLib) {
      resolve(globalThis.pdfjsLib);
      return;
    }
    const cdnUrl = `${cdnPathName}${version ? `@${version}` : ""}`, libUrl = libUrlFromArgs || `${cdnUrl}/legacy/build/pdf.mjs`, workerUrl = workerUrlFromArgs || `${cdnUrl}/legacy/build/pdf.worker.mjs`;
    documentInitParameters.cMapUrl || (documentInitParameters.cMapUrl = `${cdnUrl}/cmaps/`), documentInitParameters.standardFontDataUrl || (documentInitParameters.standardFontDataUrl = `${cdnUrl}/standard_fonts/`);
    let script = document.querySelector(`script[src="${libUrl}"]`);
    script || (script = document.createElement("script"), script.type = "module", script.src = libUrl, script.dataset.testid = "pdfjs-script", document.body.appendChild(script)), eval("class FeatureTest { static { this.supportsStaticInitialization = true } }"), script.addEventListener("load", () => {
      globalThis.pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl, globalThis.pdfjsLib.documentInitParameters = documentInitParameters, resolve(globalThis.pdfjsLib);
    }), script.addEventListener("error", (e) => {
      throw e;
    });
  } catch (e) {
    reject(e);
  }
}), PDFDocumentContext = createContext(null), PDFDocument = ({
  file: e,
  fileUrl: r,
  fileData: n,
  onError: a,
  onProxied: o,
  children: i,
  cdnPathName: c,
  version: l
}) => {
  const [p, f] = useState(null);
  return useEffect(() => {
    let u, m;
    return (async () => {
      let $;
      try {
        if (e?.type === "application/pdf")
          u = new FileReader(), $ = await new Promise((d) => {
            u.onload = (h) => {
              d(h.target.result);
            }, u.readAsArrayBuffer(e);
          });
        else if (r) {
          m = new AbortController();
          const d = await fetch(r, {
            credentials: "same-origin",
            signal: m.signal,
            headers: { "X-Is-Blocking-Call": "false" }
          });
          if (!d?.ok) {
            const b = new Error(
              d?.status && d?.statusText ? `${d.status} (${d.statusText})` : "Could not fetch file"
            );
            throw b.name = "FetchError", b;
          }
          const h = d.headers.get("Content-Type");
          (!h || h === "application/pdf") && ($ = await d.arrayBuffer());
        } else n && ($ = Uint8Array.from(atob(n), (d) => d.charCodeAt(0)).buffer);
        if (!$)
          return;
        const v = await loadPDFJSLib({ cdnPathName: c, version: l }), y = await v.getDocument({
          ...v.documentInitParameters,
          data: $
        }).promise;
        f(y), o?.(y);
      } catch (v) {
        a?.(v);
      }
    })(), () => {
      u?.abort(), m?.abort();
    };
  }, [a, o, e, r, n, c, l]), /* @__PURE__ */ jsx(PDFDocumentContext.Provider, { value: p, children: i });
}, PDFLoadingIndicator = ({ title: e, variant: r = PAGE_VARIANT, sx: n, ...a }) => /* @__PURE__ */ jsx(
  FadeInOut,
  {
    sx: {
      "&.exit, &.exiting, &.exited": {
        position: "absolute",
        zIndex: 1,
        inset: 0
      },
      ...n
    },
    ...a,
    children: /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          marginInline: "auto",
          bg: "background",
          aspectRatio: 0.77,
          position: "relative",
          ...r === "card" ? {
            borderRadius: "md",
            border: "1px solid",
            borderColor: "border.weak",
            overflow: "hidden"
          } : { boxShadow: "low" }
        },
        role: "img",
        "aria-label": e,
        children: /* @__PURE__ */ jsx(
          Flex,
          {
            sx: {
              position: "absolute",
              inset: "10%",
              overflow: "hidden",
              "div > div:nth-of-type(8n)": {
                visibility: "hidden"
              }
            },
            children: /* @__PURE__ */ jsx(
              Skeleton,
              {
                variant: "text",
                count: 15,
                sx: {
                  lineHeight: "calc(var(--relative-viewport-scale) * 1.5)",
                  "&:not(:last-child)": {
                    marginBlockEnd: "calc(var(--relative-viewport-scale) * 0.75rem)"
                  }
                }
              }
            )
          }
        )
      }
    )
  }
), usePDFDocument = () => useContext(PDFDocumentContext), abortPromise = (e, r) => {
  if (e?.aborted)
    throw r?.(), new DOMException("Aborted", "AbortError");
}, consolidateMatches = (e) => Object.keys(e).reduce(
  (r, n) => {
    const a = e[n].sort((i, c) => i.start - c.start), o = [a[0]];
    return a.slice(1).forEach((i) => {
      const c = o[o.length - 1];
      c.end === i.start - 1 ? c.end = i.end : c.end < i.start ? o.push(i) : c.end < i.end && (c.end = i.end);
    }), { ...r, [n]: o };
  },
  {}
), searchTextContent = (e, r) => {
  const n = [];
  e.map((o) => o.trim()).filter(Boolean).forEach((o) => {
    let i = 0, c = [];
    r.forEach((l, p) => {
      const f = l.str.trim();
      Array.from(f).forEach((u, m) => {
        u.toLocaleLowerCase() === o[i].toLocaleLowerCase() || /\s/.test(u) && /\s/.test(o[i]) ? (i += 1, o.length === i && (c.length && n.push(...c), n.push({
          start: Math.max(m - o.length + 1, 0),
          end: m,
          itemIndex: p
        }), c = [], i = 0)) : (i = 0, c = []);
      }), i !== 0 && (c.push({
        start: Math.max(f.length - i, 0),
        end: f.length - 1,
        itemIndex: p
      }), /\s/.test(o[i]) && (i += 1));
    });
  });
  const a = n.reduce(
    (o, i) => {
      const c = o[i.itemIndex] || [];
      return { ...o, [i.itemIndex]: [...c, i] };
    },
    {}
  );
  return consolidateMatches(a);
}, highlightText = ({
  canvasContext: e,
  viewport: r,
  textItems: n,
  highlightTerms: a,
  highlightColor: o = HIGHLIGHT_COLOR,
  renderHighlight: i
}) => {
  const c = searchTextContent(a, n);
  Object.entries(c).forEach(([l, p]) => {
    const f = n[l];
    e.font = `${f.height * r.scale}px ${f.fontName}`, e.wordSpacing = "0px";
    const u = (f.str.match(/ /g) || []).length;
    let m = 0;
    u && (m = (f.width * r.scale - e.measureText(f.str).width) / u);
    const $ = Math.ceil(e.measureText(f.str).fontBoundingBoxDescent), v = f.height * r.scale;
    let y = 0;
    p.forEach(({ start: d, end: h }, b) => {
      if (b === 0 && d > 0) {
        y += e.measureText(f.str.substring(0, d)).width;
        const I = f.str.substring(0, d).split(" ").length - 1;
        y += I * m;
      }
      if (b > 0 && p[b - 1].end + 1 < d) {
        y += e.measureText(f.str.substring(p[b - 1].end, d - 1)).width;
        const I = f.str.substring(p[b - 1].end, d - 1).split(" ").length - 1;
        y += I * m;
      }
      const _ = f.str.substring(d, h + 1), R = f.transform[4] * r.scale + y + r.viewBox[0] * r.scale, w = r.height - f.transform[5] * r.scale - v + r.viewBox[1] * r.scale, g = _.split(" ").length - 1, O = e.measureText(_).width + g * m;
      if (y += O, i)
        i(e, { x: R, y: w, width: O, height: v, descent: $, scale: r.scale });
      else {
        const { fillStyle: I, globalCompositeOperation: D } = e;
        e.globalCompositeOperation = "multiply", e.beginPath(), e.fillStyle = o, e.roundRect(R - 2, w, O + 4, v + $, 2 * r.scale), e.fill(), e.fillStyle = I, e.globalCompositeOperation = D;
      }
    });
  });
}, getPageData = async ({
  pdfDocumentProxy: e,
  pageNumber: r,
  canvasFactory: n,
  scale: a = 2,
  maxWidth: o,
  highlightTerms: i = [],
  highlightColor: c,
  renderHighlight: l,
  signal: p
}) => {
  abortPromise(p);
  const f = await e.getPage(r);
  abortPromise(p, () => {
    f.cleanup();
  });
  let u = f.getViewport({ scale: a });
  !isNaN(o) && o * a < u.width && (u = f.getViewport({ scale: o * a / u.width * a }));
  const m = n(u.width, u.height), $ = m.getContext("2d", { alpha: !1 }), v = f.render({
    canvasContext: $,
    viewport: u
  });
  abortPromise(p, () => {
    v.cancel(), f.cleanup();
  }), await v.promise, abortPromise(p, () => {
    f.cleanup();
  });
  const y = (await f.getTextContent()).items, d = y.reduce((h, b) => [...h, b.str && b.str, b.hasEOL && `
`].filter(Boolean), []).join("");
  return i.length && highlightText({ canvasContext: $, viewport: u, textItems: y, highlightTerms: i, highlightColor: c, renderHighlight: l }), f.cleanup(), {
    canvas: m,
    pageNumber: r,
    textContent: d,
    viewport: u
  };
}, isSSR = () => !!(typeof process == "object" && process + "" == "[object process]" && process.versions.node), loadIsomorphicGetDocument = async () => {
  if (isSSR())
    return (await import("pdfjs-dist/legacy/build/pdf.mjs")).getDocument;
  const { getDocument: e, ...r } = await loadPDFJSLib();
  return async (n) => e({ ...r, ...n });
}, renderPDF = async ({
  fileData: e,
  highlightTerms: r = [],
  highlightColor: n,
  renderHighlight: a,
  maxPages: o = MAX_PAGES,
  isPageIncluded: i = IS_PAGE_INCLUDED,
  scale: c = 1,
  maxWidth: l = MAX_WIDTH,
  mimeType: p = "image/webp",
  quality: f = 0.5,
  cMapUrl: u = "./node_modules/pdfjs-dist/cmaps/",
  standardFontDataUrl: m = "./node_modules/pdfjs-dist/standard_fonts/",
  signal: $,
  ...v
}) => {
  let y;
  if (abortPromise($), e && (y = Uint8Array.from(atob(e), (I) => I.charCodeAt(0)).buffer), !y)
    throw new Error("No file data provided");
  const d = await loadIsomorphicGetDocument();
  abortPromise($);
  const h = await d({ ...v, data: y, cMapUrl: u, standardFontDataUrl: m }), b = () => {
    abortPromise($, () => {
      h.destroy();
    });
  }, _ = await h.promise;
  b();
  const R = [...new Array(_.numPages)].map((I, D) => D + 1).filter(i).slice(0, o), w = (I, D) => {
    if (isSSR())
      return _.canvasFactory.create(I, D).canvas;
    const L = document.createElement("canvas");
    return L.width = I, L.height = D, L;
  }, g = R.map(
    async (I) => getPageData({ pdfDocumentProxy: _, pageNumber: I, canvasFactory: w, scale: c, maxWidth: l, highlightTerms: r, highlightColor: n, renderHighlight: a, signal: $ })
  );
  b();
  const O = {
    numPages: _.numPages,
    pages: (await Promise.all(g)).map((I) => ({
      dataUrl: I.canvas.toDataURL(p, f),
      width: I.viewport.width,
      height: I.viewport.height,
      textContent: I.textContent,
      pageNumber: I.pageNumber
    }))
  };
  return h.destroy(), O;
}, PDFPage = ({
  pageNumber: e,
  variant: r = PAGE_VARIANT,
  onError: n,
  onRender: a,
  onRendered: o,
  dataUrl: i,
  textContent: c = "",
  highlightTerms: l = [],
  highlightColor: p,
  renderHighlight: f,
  scale: u = globalThis.devicePixelRatio,
  width: m,
  "data-testid": $ = "pdf-page",
  "aria-label": v,
  ...y
}) => {
  const d = (typeof useId == "function" ? useId : useId.default)(), [h, b] = useState(!i), [_, R] = useState(c), w = useRef(null), g = useRef(null), O = useRef(!1), I = useI18n(), D = usePDFDocument(), L = l?.join?.() || "";
  return useEffect(
    () => {
      i || (async () => {
        O.current = !0, b(!0);
        try {
          a?.(e);
          const { textContent: j } = await getPageData({
            pdfDocumentProxy: D,
            pageNumber: e,
            canvasFactory: (M, F) => (g.current.width = M, g.current.height = F, g.current),
            scale: u,
            highlightColor: p,
            renderHighlight: f,
            highlightTerms: l
          });
          R(j), o?.(e);
        } catch (j) {
          n?.(j);
        } finally {
          b(!1), O.current = !1;
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [n, a, o, e, D, L, f, u, i]
  ), /* @__PURE__ */ jsx(
    Box,
    {
      ref: w,
      "data-testid": $,
      "data-page-number": e,
      role: "img",
      "aria-roledescription": I.pgettext("aria-roledescription for PDF page preview", "preview"),
      "aria-label": v,
      "aria-busy": h,
      ...!!(c || _) && {
        "aria-describedby": d
      },
      ...y,
      children: /* @__PURE__ */ jsxs(
        Flex,
        {
          sx: {
            ...r === "card" ? {
              borderRadius: "md",
              border: "1px solid",
              borderColor: "border.weak",
              overflow: "hidden"
            } : { boxShadow: "low" },
            marginInline: "auto",
            maxInlineSize: m,
            "canvas, img": {
              inlineSize: "100%",
              "&[hidden]": {
                visibility: "hidden"
              }
            }
          },
          children: [
            i ? /* @__PURE__ */ jsx(Box, { as: "img", alt: v, "data-testid": `${$}-image`, src: i }) : /* @__PURE__ */ jsx(Box, { as: "canvas", ref: g, hidden: h, "data-testid": `${$}-canvas`, children: v }),
            !!(c || _) && /* @__PURE__ */ jsx(VisuallyHidden, { id: d, children: c || _ })
          ]
        }
      )
    }
  );
}, INPUT_DEBOUNCE = 350, PageablePDFPreview = ({
  file: e,
  fileUrl: r,
  fileData: n,
  fileName: a,
  variant: o = PAGE_VARIANT,
  highlightTerms: i = [],
  highlightColor: c,
  renderHighlight: l,
  onClick: p,
  onPageRendered: f,
  onError: u,
  carouselProps: m,
  sx: $,
  cdnPathName: v,
  version: y,
  "data-testid": d = "pageable-pdf-preview",
  ...h
}) => {
  const [b, _] = useState(0), [R, w] = useState(0), [g, O] = useState(!1), [I, D] = useState(!1), [L, j] = useState("1"), [M, F] = useState(globalThis.devicePixelRatio), U = useRef({}), K = useRef(), W = useRef(null), N = useI18n();
  useEffect(() => {
    _(0), j("1"), w(0), O(!!(e || r || n)), D(!!(e || r || n));
  }, [e, r, n]);
  const V = useCallback(async (T) => {
    const B = T[0].target.clientWidth;
    let Y = globalThis.devicePixelRatio;
    B >= COMMON_DOCUMENT_WIDTH * 2 ? Y *= 4 : B >= COMMON_DOCUMENT_WIDTH ? Y *= 2 : B >= COMMON_DOCUMENT_WIDTH / 2 || (B >= COMMON_DOCUMENT_WIDTH / 4 ? Y *= 0.5 : Y *= 0.25), F(Y);
  }, []);
  useEffect(() => {
    const T = new ResizeObserver(V);
    return W.current && T.observe(W.current), () => {
      T.disconnect();
    };
  }, [V]);
  const H = useCallback((T) => {
    O(!1), w(Math.min(T.numPages, 1230));
  }, []), J = useMemo(
    () => ({
      threeUpPage: N.pgettext("aria-roledescription for PDF page", "page"),
      slideRoleTranslation: N.pgettext("aria-roledescription for PDF page", "page"),
      of: (T, B) => N.pgettext("aria-label for PDF page preview", "{0} of {1}", [T, B]),
      chevronLeft: N.pgettext("aria-label for PDF preview", "Preview previous page"),
      chevronRight: N.pgettext("aria-label for PDF preview", "Preview next page"),
      ...m?.ariaLabels
    }),
    [m?.ariaLabels, N]
  ), Z = useCallback((T) => {
    U.current[T] = Date.now();
  }, []), G = useCallback(
    (T) => {
      D(!1), f?.({ time: Date.now() - U.current[T], pageNumber: T, numPages: R });
    },
    [R, f]
  ), Q = useCallback(
    (T) => {
      u?.(T), O(!1), D(!1);
    },
    [u]
  ), q = useCallback(
    (T) => {
      typeof K.current < "u" && clearTimeout(K.current), j(T.target.value);
      const B = Math.max(0, Math.min(parseInt(T.target.value.trim(), 10) - 1, R - 1));
      isNaN(B) || (K.current = setTimeout(() => {
        _(B);
      }, INPUT_DEBOUNCE));
    },
    [R]
  );
  useEffect(
    () => () => {
      typeof K.current < "u" && clearTimeout(K.current);
    },
    []
  );
  const X = i?.join?.() || "", A = l?.toString?.() || "", z = useMemo(
    () => [...Array(R)].map((T, B) => {
      if (B === 0 || B === b || B === b - 1 || B === b + 1)
        return /* @__PURE__ */ jsx(
          PDFPage,
          {
            sx: { margin: o === "page" ? 1 : 0 },
            onClick: p,
            onError: Q,
            onRender: Z,
            onRendered: G,
            pageNumber: B + 1,
            variant: o,
            highlightTerms: i,
            highlightColor: c,
            renderHighlight: l,
            scale: M,
            "aria-label": N.pgettext("aria-label for PDF page preview", "preview of page {0} of {1}", [B + 1, R]),
            "data-testid": `${d}-page`
          },
          `page-${B}`
        );
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      R,
      b,
      p,
      u,
      Z,
      G,
      X,
      c,
      A,
      N,
      d
    ]
  );
  return /* @__PURE__ */ jsx(
    PDFDocument,
    {
      file: e,
      fileUrl: r,
      fileData: n,
      onProxied: H,
      onError: Q,
      cdnPathName: v,
      version: y,
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          as: "section",
          role: "group",
          "aria-label": a || e?.name || "PDF",
          "aria-roledescription": N.pgettext("aria-roledescription for PDF document", "document"),
          "data-testid": d,
          sx: { position: "relative", zIndex: 0, ...$ },
          style: { "--relative-viewport-scale": M / globalThis.devicePixelRatio },
          ref: W,
          ...h,
          children: [
            z.length === 1 && z[0],
            z.length > 1 && /* @__PURE__ */ jsxs(Box, { sx: { opacity: 1, transition: "opacity 250ms", ...I && { opacity: 0, height: 0, overflow: "hidden" } }, children: [
              /* @__PURE__ */ jsx(
                E,
                {
                  positionControl: "inside",
                  index: b,
                  onIndexChange: (T) => {
                    _(T), j((T + 1).toString());
                  },
                  sx: {
                    "& > div": {
                      minBlockSize: "2.5rem"
                    },
                    button: {
                      marginInline: 2
                    },
                    "[data-testid^=ipl-carousel-slide]": {
                      flexGrow: 1,
                      flexShrink: 0,
                      flexBasis: 0
                    },
                    ...m?.sx
                  },
                  items: z,
                  ariaLabels: J,
                  showDots: !1,
                  showCharacterCounter: !1
                }
              ),
              R > 1 && /* @__PURE__ */ jsxs(Flex, { sx: { justifyContent: "center", alignItems: "center", gap: 1, marginInline: 1, marginBlockStart: 2 }, children: [
                /* @__PURE__ */ jsx(
                  Input,
                  {
                    "aria-label": N.pgettext("aria-label for current page input", "Current page"),
                    size: "sm",
                    type: "number",
                    step: 1,
                    min: 1,
                    max: R,
                    value: L,
                    onChange: q,
                    onBlur: () => j((b + 1).toString()),
                    "data-testid": `${d}-page-input`,
                    invalid: !1,
                    sx: {
                      "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
                        display: "none"
                      },
                      input: {
                        MozAppearance: "textfield",
                        inlineSize: `${R.toString().length + 1}em`
                      }
                    }
                  }
                ),
                /* @__PURE__ */ jsxs(Text, { level: 3, "aria-hidden": !0, children: [
                  "/ ",
                  R
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsx(AnimatePresence, { children: (g || I) && /* @__PURE__ */ jsx(
              PDFLoadingIndicator,
              {
                variant: o,
                "data-testid": `${d}-document-loading`,
                title: N.gettext("Loading document")
              }
            ) })
          ]
        }
      )
    }
  );
}, PAGES = [], PDFPreview = ({
  file: e,
  fileUrl: r,
  fileData: n,
  fileName: a,
  pages: o = PAGES,
  isPageIncluded: i = IS_PAGE_INCLUDED,
  maxPages: c = MAX_PAGES,
  numPages: l = 0,
  hideUnrenderedPageCount: p,
  highlightTerms: f = [],
  highlightColor: u,
  renderHighlight: m,
  variant: $ = PAGE_VARIANT,
  onClick: v,
  onRendered: y,
  onError: d,
  sx: h,
  cdnPathName: b,
  version: _,
  "data-testid": R = "pdf-preview",
  ...w
}) => {
  const [g, O] = useState(0), [I, D] = useState([]), [L, j] = useState(!o.length), [M, F] = useState(!o.length), [U, K] = useState(globalThis.devicePixelRatio), W = useRef({}), N = useRef(0), V = useRef(null), H = useI18n();
  useEffect(() => {
    N.current = Date.now(), j(!!(e || r || n)), F(!!(e || r || n));
  }, [e, r, n]);
  const J = useCallback(async (A) => {
    const z = A[0].target.clientWidth;
    let T = globalThis.devicePixelRatio;
    z >= COMMON_DOCUMENT_WIDTH * 2 ? T *= 4 : z >= COMMON_DOCUMENT_WIDTH ? T *= 2 : z >= COMMON_DOCUMENT_WIDTH / 2 || (z >= COMMON_DOCUMENT_WIDTH / 4 ? T *= 0.5 : T *= 0.25), K(T);
  }, []);
  useEffect(() => {
    const A = new ResizeObserver(J);
    return V.current && A.observe(V.current), () => {
      A.disconnect();
    };
  }, [J]);
  const Z = useCallback(
    (A) => {
      O(A.numPages), j(!1);
      const z = [...new Array(A.numPages)].map((T, B) => ({ pageNumber: B + 1 })).filter(({ pageNumber: T }) => i(T)).slice(0, c);
      D(z), N.current = Date.now() - N.current;
    },
    [c, i]
  ), G = useCallback(
    (A) => {
      d?.(A), j(!1), F(!1);
    },
    [d]
  ), Q = useCallback((A) => {
    W.current = { ...W.current, [A]: Date.now() };
  }, []), q = useCallback(
    (A) => {
      N.current += Date.now() - W.current[A], delete W.current[A], Object.keys(W.current).length === 0 && (F(!1), y?.({ time: N.current, renderedPageNumbers: I.map(({ pageNumber: z }) => z), numPages: g }), N.current = 0);
    },
    [g, y, I]
  ), X = (l || g) - (o.length || I.length);
  return /* @__PURE__ */ jsx(
    Flex,
    {
      as: "section",
      role: "group",
      "aria-label": a || e?.name || "PDF",
      "aria-roledescription": H.pgettext("aria-roledescription for PDF document", "document"),
      "data-testid": R,
      sx: { flexDirection: "column", gap: 2, position: "relative", ...h },
      style: { "--relative-viewport-scale": U / globalThis.devicePixelRatio },
      ref: V,
      ...w,
      children: (e || r || n || !!o.length) && /* @__PURE__ */ jsx(
        PDFDocument,
        {
          file: e,
          fileUrl: r,
          fileData: n,
          onProxied: Z,
          onError: G,
          cdnPathName: b,
          version: _,
          children: /* @__PURE__ */ jsxs(AnimatePresence, { children: [
            (L || M) && /* @__PURE__ */ jsx(
              PDFLoadingIndicator,
              {
                variant: $,
                title: H.gettext("Loading document"),
                "data-testid": `${R}-document-loading`
              }
            ),
            (o?.length ? o : I).map((A) => /* @__PURE__ */ jsx(FadeInOut, { children: /* @__PURE__ */ jsx(
              PDFPage,
              {
                onClick: v,
                ...A,
                scale: U,
                "aria-label": H.pgettext("aria-label for PDF page preview", "preview of page {0} of {1}", [
                  A.pageNumber,
                  l || g
                ]),
                highlightTerms: f,
                highlightColor: u,
                renderHighlight: m,
                onError: G,
                onRender: Q,
                onRendered: q,
                variant: $,
                "data-testid": `${R}-page`
              },
              `page-${A.pageNumber}`
            ) }, `page-${A.pageNumber}`)),
            !M && !p && X > 0 && /* @__PURE__ */ jsx(FadeInOut, { children: /* @__PURE__ */ jsx(Text, { as: "div", variant: "secondary", level: 1, sx: { textAlign: "center" }, "data-testid": `${R}-more-pages`, children: H.ngettext("+{0} more page", "+{0} more pages", X, [X]) }) })
          ] })
        }
      )
    }
  );
};
export {
  CDN_PATH_NAME,
  COMMON_DOCUMENT_WIDTH,
  HIGHLIGHT_COLOR,
  IS_PAGE_INCLUDED,
  MAX_PAGES,
  MAX_WIDTH,
  PAGE_VARIANT,
  PDFJS_VERSION,
  PDFPreview,
  PageablePDFPreview,
  loadPDFJSLib,
  renderPDF
};
//# sourceMappingURL=index.mjs.map
